// src/components/UnifiedCartWindow.js
import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes, css } from 'styled-components';
import {
  Check, X, Minus, CreditCard,
  CheckCircle, Package, Send,
  Banknote, ShieldCheck, QrCode, Sparkles,
  Wifi, Smartphone,
  MapPin, Clock, Car, Users, Bell, Navigation, Timer, Zap, Flag, Wrench,
  Truck, ArrowRight, ChevronDown, ChevronUp, Trash2, Plus, Store
} from 'lucide-react';
import StripePaymentForm from './Stripe/StripePaymentForm';
import TreasureMap from './map/TreasureMap';
import { db } from '../firebase/config';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { TransactionService } from '../services/TransactionService';
import { formatShopPrice } from '../utils/currency';
import { getOrderDetailsEntries, getItemFormFields } from '../utils/orderFormConfig';
import { useLocation as useUserLocation } from '../contexts/LocationContext';

// Order-form field keys that hold an actual pinpointable address (as opposed to
// a text description like "Vehicle" or "Coverage Area") — these get the
// current-location + map-pick affordances in the options editor.
const LOCATION_FIELD_KEYS = new Set(['pickupLocation', 'dropoffLocation', 'serviceAddress', 'eventAddress']);

// ─── palette ──────────────────────────────────────────────────────────────────
const PAL = {
  pending_send:              { a: '#F59E0B', step: 0, word: 'DRAFT'     },
  pending_seller_acceptance: { a: '#60A5FA', step: 1, word: 'PENDING'   },
  seller_accepted:           { a: '#34D399', step: 2, word: 'CONFIRMED' },
  paid:                      { a: '#A78BFA', step: 3, word: 'PAID'      },
  completed:                 { a: '#4ADE80', step: 4, word: 'DONE'      },
};
const pa    = s => PAL[s] ?? { a: '#9CA3AF', step: 0, word: 'ORDER' };

// ─── pickup payment options ────────────────────────────────────────────────────
const PAY_LABELS = { zelle: 'Zelle', venmo: 'Venmo', cash: 'Cash', card: 'Card' };
const PAY_OPTIONS = [
  { id: 'zelle', label: 'Zelle on pick up', handleKey: 'zelleId' },
  { id: 'venmo', label: 'Venmo on pick up', handleKey: 'venmoId' },
  { id: 'cash',  label: 'Cash on pick up' },
  { id: 'card',  label: 'Card on pick up' },
];

// ─── keyframes ────────────────────────────────────────────────────────────────
const fadeUp = keyframes`
  from { opacity:0; transform:translateY(8px); }
  to   { opacity:1; transform:translateY(0); }
`;
const slideRow = keyframes`
  from { opacity:0; transform:translateX(-4px); }
  to   { opacity:1; transform:translateX(0); }
`;
const blink = keyframes`
  0%,100% { opacity:1; }
  50%      { opacity:0; }
`;
const drawCheck = keyframes`
  from { stroke-dashoffset: 60; }
  to   { stroke-dashoffset: 0; }
`;
const marchTop = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 40px 0; }
`;
const marchBottom = keyframes`
  from { background-position: 0 0; }
  to   { background-position: -40px 0; }
`;
const marchLeft = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 0 40px; }
`;
const marchRight = keyframes`
  from { background-position: 0 0; }
  to   { background-position: 0 -40px; }
`;
const dotBounce = keyframes`
  0%,80%,100% { transform:translateY(0); opacity:0.3; }
  40%          { transform:translateY(-4px); opacity:1; }
`;
const zoomBgIn  = keyframes` from { opacity:0; } to { opacity:1; } `;
const zoomBgOut = keyframes` from { opacity:1; } to { opacity:0; } `;
const zoomImgIn  = keyframes`
  from { opacity:0; transform:scale(0.85); }
  to   { opacity:1; transform:scale(1); }
`;
const zoomImgOut = keyframes`
  from { opacity:1; transform:scale(1); }
  to   { opacity:0; transform:scale(0.9); }
`;
// Sweep used by the welcome page's own skeleton cards — a translateX pass
// reads as a smoother "flowing" highlight than animating background-position.
const shimmer = keyframes`
  100% { transform: translateX(100%); }
`;

// Full-box loading placeholder: a flat tint plus the sweeping highlight above.
// translateZ(0) forces its own compositing layer so overflow:hidden clips the
// ::after sweep at the compositor level — otherwise some mobile browsers
// promote it separately and the highlight escapes the rounded box.
const ShimmerOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255,255,255,0.06);
  overflow: hidden;
  transform: translateZ(0);
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.13) 50%, transparent 80%);
    transform: translateX(-100%);
    animation: ${shimmer} 1.6s infinite linear;
  }
`;

// ─── marching ants mixin ──────────────────────────────────────────────────────
const marchingAnts = (color) => css`
  position: relative;
  &::before, &::after { content:''; position:absolute; pointer-events:none; }
  &::before {
    inset: 0;
    background:
      repeating-linear-gradient(90deg, ${color} 0, ${color} 6px, transparent 6px, transparent 12px) top / 12px 1px no-repeat,
      repeating-linear-gradient(90deg, ${color} 0, ${color} 6px, transparent 6px, transparent 12px) bottom / 12px 1px no-repeat,
      repeating-linear-gradient(0deg,  ${color} 0, ${color} 6px, transparent 6px, transparent 12px) left  / 1px 12px no-repeat,
      repeating-linear-gradient(0deg,  ${color} 0, ${color} 6px, transparent 6px, transparent 12px) right / 1px 12px no-repeat;
    background-position: 0 0, 0 100%, 0 0, 100% 0;
    border-radius: inherit;
    animation:
      ${marchTop}    0.6s linear infinite,
      ${marchBottom} 0.6s linear infinite,
      ${marchLeft}   0.6s linear infinite,
      ${marchRight}  0.6s linear infinite;
    opacity: 0.65;
  }
`;

// ─── outer card ───────────────────────────────────────────────────────────────
const Card = styled.div`
  width: 100%;
  border-radius: 14px;
  overflow: hidden;
  background: #26262b;
  border: 1px solid rgba(255,255,255,0.07);
  box-shadow: 0 16px 48px rgba(0,0,0,0.6), 0 2px 0 rgba(255,255,255,0.04) inset;
  animation: ${fadeUp} 0.26s cubic-bezier(0.22,1,0.36,1);
  position: relative;
`;


const Inner = styled.div`position: relative; z-index: 1;`;

// ─── header strip ─────────────────────────────────────────────────────────────
const Strip = styled.div`
  padding: 0.88rem 1rem 0.82rem 1.1rem;
  background: linear-gradient(135deg, ${({ $a }) => $a}18 0%, transparent 70%);
  border-bottom: 1px solid rgba(255,255,255,0.06);
  display: flex;
  align-items: center;
  gap: 0.65rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: ${({ $a }) => $a};
    box-shadow: 0 0 10px ${({ $a }) => $a}88;
  }

  .left {
    flex: 1;
    min-width: 0;
    .title { font-size: 0.96rem; font-weight: 700; color: #fff; }
    .sub   { font-size: 0.74rem; color: rgba(255,255,255,0.38); margin-top: 3px; }
  }
`;

const StatusWord = styled.div`
  font-size: 0.66rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  color: ${({ $a }) => $a};
  background: ${({ $a }) => $a}14;
  border: 1px solid ${({ $a }) => $a}30;
  padding: 0.2rem 0.55rem;
  border-radius: 4px;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;

  .live {
    width: 5px; height: 5px;
    border-radius: 50%;
    background: currentColor;
    ${({ $live }) => $live && css`animation: ${blink} 1.1s step-end infinite;`}
  }
`;

// Pulsing dot shown on the minimized header when something changed underneath
const ChangeDot = styled.span`
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #F87171;
  box-shadow: 0 0 0 3px rgba(248,113,113,0.25);
  flex-shrink: 0;
  animation: ${blink} 1s step-end infinite;
`;

// ─── receipt section ──────────────────────────────────────────────────────────
const Receipt = styled.div`
  padding: 0.3rem 0 0;
`;

const SectionHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1rem 0.2rem;

  .label {
    font-size: 0.64rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.3);
  }
  .badge {
    font-size: 0.68rem;
    font-weight: 700;
    color: ${({ $a }) => $a};
    background: ${({ $a }) => $a}15;
    border: 1px solid ${({ $a }) => $a}28;
    padding: 1px 7px;
    border-radius: 3px;
  }
`;

const Dash = styled.div`
  margin: 0.35rem 1rem 0;
  height: 1px;
  background: repeating-linear-gradient(
    90deg,
    rgba(255,255,255,0.08) 0px,
    rgba(255,255,255,0.08) 4px,
    transparent 4px,
    transparent 8px
  );
`;

// item row — image-on-top card layout: big photo on its own line, details + price below
const IRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  border-bottom: 2px solid ${({ $a }) => $a}30;
  animation: ${slideRow} 0.25s cubic-bezier(0.22,1,0.36,1) both;
  ${({ $i }) => css`animation-delay: ${$i * 55}ms;`}

  &:last-child { border-bottom: none; }
  &:hover { background: rgba(255,255,255,0.02); }

  /* image — own line, quarter width, centered */
  .img-wrap { padding: 0.85rem 1rem 0; display: flex; justify-content: center; }
  .img-box {
    position: relative;
    width: 31.25%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    overflow: hidden;
    border: 1px solid rgba(255,255,255,0.11);
    box-shadow: 0 6px 20px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.07) inset;
    background: rgba(255,255,255,0.03);
  }
  .thumb {
    position: relative;
    z-index: 1;
    width: 100%; height: 100%;
    object-fit: cover;
    display: block;
    opacity: 0;
    transition: opacity 0.35s ease;
    &.is-loaded { opacity: 1; }
  }
  .ph {
    position: relative;
    width: 31.25%;
    aspect-ratio: 16 / 9;
    border-radius: 12px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
    display: flex; align-items: center; justify-content: center;
    color: rgba(255,255,255,0.12);
  }
  .remove-btn {
    position: absolute;
    top: 8px; right: 8px;
    z-index: 2;
    width: 28px; height: 28px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.18);
    background: rgba(10,10,14,0.72);
    color: rgba(255,255,255,0.75);
    display: flex; align-items: center; justify-content: center;
    cursor: pointer;
    transition: background 0.15s, color 0.15s, opacity 0.15s;
    &:hover:not(:disabled) { background: rgba(248,113,113,0.85); color: #fff; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }

  /* details line — name/tags on the left, price on the right */
  .details-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1rem 0.8rem;
  }
  .body {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.32rem;
  }
  .name {
    font-size: 0.98rem;
    font-weight: 700;
    color: #fff;
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
  }
  .tags { display: flex; flex-wrap: wrap; gap: 3px; }
  .unit {
    font-size: 0.73rem;
    color: rgba(255,255,255,0.38);
    font-variant-numeric: tabular-nums;
    font-family: 'SF Mono', monospace;
    display: flex; align-items: center; gap: 5px;
  }

  /* price column */
  .price-col {
    text-align: right;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 0.3rem;
  }
  .total {
    font-size: 1.05rem; font-weight: 800; color: #fff;
    font-variant-numeric: tabular-nums; font-family: 'SF Mono', monospace;
    letter-spacing: -0.02em;
  }
  .qty-ctrl {
    display: flex; align-items: center; gap: 4px;
    button {
      width: 24px; height: 24px;
      border-radius: 6px; border: 1px solid rgba(255,255,255,0.1);
      background: rgba(255,255,255,0.04); color: rgba(255,255,255,0.5);
      cursor: pointer; display: flex; align-items: center; justify-content: center;
      transition: all 0.15s;
      svg { width:11px; height:11px; }
      &:hover:not(:disabled) { background:rgba(255,255,255,0.1); color:#fff; }
      &:disabled { opacity:0.2; cursor:not-allowed; }
    }
    .n { min-width:20px; text-align:center; font-size:0.78rem; font-weight:700; color:#fff; }
  }
`;

// item row — skeleton placeholder shown while item data hasn't resolved yet.
// Mirrors IRow's real layout (image on its own line, then a details-row with
// name/tag on the left and a price column on the right) so the loading state
// doesn't jump/reflow once the real content lands.
const SkelRow = styled.div`
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }

  .img-wrap { padding: 0.85rem 1rem 0; display: flex; justify-content: center; }
  .img-box {
    position: relative; width: 31.25%; aspect-ratio: 16 / 9; border-radius: 12px;
    overflow: hidden; background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.07);
  }
  .details-row {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.65rem 1rem 0.8rem;
  }
  .body { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 0.42rem; }
  .price-col { flex-shrink: 0; display: flex; flex-direction: column; align-items: flex-end; gap: 0.4rem; }
  .bar {
    position: relative;
    overflow: hidden;
    border-radius: 4px;
    background: rgba(255,255,255,0.06);
    transform: translateZ(0);
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(90deg, transparent 20%, rgba(255,255,255,0.13) 50%, transparent 80%);
      transform: translateX(-100%);
      animation: ${shimmer} 1.6s infinite linear;
    }
  }
  .bar.name  { width: 70%; height: 13px; }
  .bar.tag   { width: 38%; height: 16px; border-radius: 10px; }
  .bar.price { width: 46px; height: 15px; }
  .bar.qty   { width: 58px; height: 22px; border-radius: 6px; }
`;

const CTag = styled.span`
  font-size: 0.67rem; font-weight: 600;
  padding: 2px 7px; border-radius: 4px;
  max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  ${({ $o }) => $o ? css`
    background:rgba(52,211,153,0.1); border:1px solid rgba(52,211,153,0.2); color:#6EE7B7;
  ` : css`
    background:rgba(251,191,36,0.09); border:1px solid rgba(251,191,36,0.17); color:#FDE68A;
  `}
`;

const OptionsToggleBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  margin-top: 3px;
  padding: 0;
  background: none;
  border: none;
  color: ${({ $a }) => $a};
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
  svg { width: 12px; height: 12px; }
  &:hover { opacity: 0.8; }
`;

const OptionsEditor = styled.div`
  margin-top: 0.5rem;
  padding: 0.65rem 0.7rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.08);
  display: flex;
  flex-direction: column;
  gap: 0.65rem;

  .field-label {
    font-size: 0.66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: rgba(255,255,255,0.4);
    margin-bottom: 0.32rem;
  }
  .chips { display: flex; flex-wrap: wrap; gap: 0.35rem; }
  .chip {
    padding: 0.28rem 0.7rem;
    border-radius: 20px;
    font-size: 0.76rem;
    cursor: pointer;
    border: 1px solid rgba(255,255,255,0.18);
    background: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.7);
    transition: all 0.15s;
    &.selected {
      border-color: ${({ $a }) => $a};
      background: ${({ $a }) => $a}2A;
      color: #fff;
      font-weight: 700;
    }
  }
  input[type="text"], input[type="date"], input[type="datetime-local"], textarea, select {
    width: 100%;
    background: rgba(255,255,255,0.05);
    border: 1px solid rgba(255,255,255,0.12);
    border-radius: 8px;
    padding: 0.5rem 0.6rem;
    color: #fff;
    font-size: 0.82rem;
    font-family: inherit;
    &:focus { outline: none; border-color: ${({ $a }) => $a}; }
  }
  textarea { resize: vertical; min-height: 60px; }
  select { appearance: none; }
  .stepper { display: flex; align-items: center; gap: 0.7rem; }
  .stepper button {
    width: 26px; height: 26px; border-radius: 50%; flex-shrink: 0;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.05); color: #fff;
    display: flex; align-items: center; justify-content: center; cursor: pointer;
  }
  .stepper .n { font-size: 0.85rem; font-weight: 700; min-width: 20px; text-align: center; }
  .toggle-row { display: flex; align-items: center; justify-content: space-between; cursor: pointer; }
  .toggle-row input { width: 16px; height: 16px; accent-color: ${({ $a }) => $a}; }
  .location-row { display: flex; align-items: center; gap: 0.4rem; }
  .location-row input[type="text"] { flex: 1; }
  .location-row button {
    flex-shrink: 0;
    width: 34px; height: 34px; border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.15);
    background: rgba(255,255,255,0.05); color: ${({ $a }) => $a};
    display: flex; align-items: center; justify-content: center; cursor: pointer;
    transition: background 0.15s;
    &:hover:not(:disabled) { background: ${({ $a }) => $a}22; }
    &:disabled { opacity: 0.4; cursor: not-allowed; }
  }
  .actions { display: flex; gap: 0.5rem; margin-top: 0.15rem; }
  .actions > div { flex: 1; }
`;

// ─── totals ───────────────────────────────────────────────────────────────────
const AddMoreItemsBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  width: 80%;
  margin: 0.75rem auto;
  padding: 0.55rem 0.9rem;
  background: ${p => `${p.$a}14`};
  border: 1px solid ${p => `${p.$a}40`};
  border-radius: 10px;
  color: ${p => p.$a};
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: ${p => `${p.$a}22`};
    border-color: ${p => `${p.$a}70`};
  }

  .avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    overflow: hidden;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${p => `${p.$a}22`};
    border: 1px solid ${p => `${p.$a}55`};

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }
`;

const Totals = styled.div`
  margin: 0.1rem 0 0;
  padding: 0 1rem 0;

  .row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 0.32rem 0;
    font-size: 0.8rem;
    .l { color: rgba(255,255,255,0.42); }
    .v { color: rgba(255,255,255,0.72); font-weight: 600; font-variant-numeric: tabular-nums; font-family: 'SF Mono', monospace; }
    .free { color: #6EE7B7; font-weight: 700; }
  }
`;

const TotalLine = styled.div`
  margin: 0.35rem 1rem 0;
  height: 1px;
  background: rgba(255,255,255,0.09);
`;

const GrandTotal = styled.div`
  padding: 0.5rem 1rem 0.7rem;
  display: flex;
  justify-content: space-between;
  align-items: baseline;

  .label {
    font-size: 0.77rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.5);
  }

  .amount {
    font-size: 1.55rem;
    font-weight: 800;
    color: #fff;
    font-family: 'SF Mono', 'Courier New', monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
    line-height: 1;
  }
`;

// ─── state panels ─────────────────────────────────────────────────────────────
const StateDivider = styled.div`
  height: 1px;
  margin: 0 1rem;
  background: linear-gradient(to right, transparent, rgba(255,255,255,0.08), transparent);
`;

const StatePanel = styled.div`
  padding: 0.82rem 1rem;
  animation: ${fadeUp} 0.24s ease both;
`;

// pending (seller) — marching ants wrapper
const MarchBox = styled.div`
  border-radius: 10px;
  padding: 0.82rem 0.9rem;
  background: rgba(96,165,250,0.06);
  ${({ $a }) => marchingAnts($a)}
`;

// waiting cursor
const WaitText = styled.div`
  font-size: 0.86rem;
  font-weight: 600;
  color: ${({ $a }) => $a};
  display: flex;
  align-items: center;
  gap: 2px;

  .cursor {
    display: inline-block;
    width: 2px;
    height: 1em;
    background: currentColor;
    margin-left: 3px;
    vertical-align: text-bottom;
    animation: ${blink} 0.9s step-end infinite;
  }
`;

// three bouncing dots (no circle — just ···)
const Dots = styled.span`
  display: inline-flex;
  gap: 3px;
  align-items: center;
  span {
    display: inline-block;
    width: 4px; height: 4px;
    border-radius: 50%;
    background: currentColor;
    animation: ${dotBounce} 1.1s ease-in-out infinite;
    &:nth-child(1) { animation-delay: 0ms; }
    &:nth-child(2) { animation-delay: 140ms; }
    &:nth-child(3) { animation-delay: 280ms; }
  }
`;


// proper draw check using styled
const AnimPolyline = styled.polyline`
  stroke-dasharray: 60;
  stroke-dashoffset: 60;
  animation: ${drawCheck} 0.5s cubic-bezier(0.22,1,0.36,1) 0.12s forwards;
`;

const CheckMark = ({ color, size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 38 38" fill="none">
    <circle cx="19" cy="19" r="17" stroke={color} strokeWidth="1.5" opacity="0.2" />
    <circle cx="19" cy="19" r="17" stroke={color} strokeWidth="1.5" opacity="0.08" fill={color} />
    <AnimPolyline
      points="11,19 17,25 27,13"
      stroke={color}
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// info row in state panels
const InfoLine = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.55rem;
  padding: 0.28rem 0;
  font-size: 0.8rem;

  .ico { color: ${({ $a }) => $a}; flex-shrink: 0; margin-top: 1px; }
  .txt { color: rgba(255,255,255,0.6); line-height: 1.5; }
  .bold { color: rgba(255,255,255,0.88); font-weight: 600; }
`;

const PanelTitle = styled.div`
  font-size: 0.92rem;
  font-weight: 700;
  color: ${({ $a }) => $a};
  margin-bottom: 7px;
`;

// payout breakdown row
const PayRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.26rem 0;
  font-size: 0.78rem;
  .l { color: rgba(255,255,255,0.42); }
  .v { color: rgba(255,255,255,0.72); font-weight: 600; font-family:'SF Mono',monospace; }
  &.hi .l { color: rgba(255,255,255,0.75); font-weight:700; font-size:0.85rem; }
  &.hi .v { color: #4ADE80; font-weight:800; font-size:0.94rem; }
`;

// ─── actions ─────────────────────────────────────────────────────────────────
const AZ = styled.div`
  padding: 0.72rem 1rem 0.88rem;
  display: flex;
  flex-direction: column;
  gap: 0.38rem;
`;

const Btn = styled.button`
  width: 100%;
  padding: ${({ $sm }) => $sm ? '0.58rem' : '0.74rem'};
  border-radius: 10px;
  font-size: ${({ $sm }) => $sm ? '0.8rem' : '0.86rem'};
  font-weight: 700;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  gap: 0.4rem;
  transition: all 0.18s cubic-bezier(0.22,1,0.36,1);
  letter-spacing: 0.01em;
  svg { width:14px; height:14px; }

  ${({ $v, $a }) => $v === 'primary' ? css`
    background: ${$a};
    color: #000;
    border: none;
    box-shadow: 0 3px 14px ${$a}44, 0 1px 0 rgba(255,255,255,0.15) inset;
    &:hover:not(:disabled) { filter:brightness(1.08); transform:translateY(-2px); box-shadow:0 6px 20px ${$a}55; }
    &:active:not(:disabled) { transform:translateY(0); }
    &:disabled { opacity:0.38; cursor:not-allowed; transform:none; box-shadow:none; }
  ` : $v === 'danger' ? css`
    background: transparent;
    border: 1px solid rgba(248,113,113,0.28);
    color: #F87171;
    &:hover:not(:disabled) { background:rgba(248,113,113,0.07); border-color:rgba(248,113,113,0.5); }
    &:disabled { opacity:0.28; cursor:not-allowed; }
  ` : css`
    background: transparent;
    border: 1px solid rgba(255,255,255,0.08);
    color: rgba(255,255,255,0.45);
    &:hover:not(:disabled) { background:rgba(255,255,255,0.05); color:rgba(255,255,255,0.8); }
    &:disabled { opacity:0.25; cursor:not-allowed; }
  `}
`;

const ExpandSection = styled.div`
  overflow: hidden;
  max-height: ${({ $o }) => $o ? '900px' : '0'};
  transition: max-height 0.4s cubic-bezier(0.22,1,0.36,1);
`;

const Gap = ({ n = 0.7 }) => <div style={{ height: `${n}rem` }} />;

// ─── Parse NDEF records from an NFC reading event ─────────────────────────
const parseNfcEvent = (event) => {
  const result = { serialNumber: event.serialNumber || null, records: [], token: null, url: null, text: null };
  const decoder = new TextDecoder();
  for (const record of (event.message?.records || [])) {
    try {
      if (record.recordType === 'text') {
        const text = decoder.decode(record.data);
        result.text = result.text || text;
        result.records.push({ type: 'text', value: text });
        // Some NDEF payment tags carry a JSON token
        try { const j = JSON.parse(text); if (j.token || j.paymentToken) result.token = j.token || j.paymentToken; } catch {}
      } else if (record.recordType === 'url') {
        const url = decoder.decode(record.data);
        result.url = url;
        result.records.push({ type: 'url', value: url });
      } else if (record.recordType === 'mime') {
        // application/vnd.* payment records
        try {
          const raw = decoder.decode(record.data);
          result.records.push({ type: record.mediaType, value: raw });
          try { const j = JSON.parse(raw); if (j.token) result.token = j.token; } catch {}
        } catch {}
      } else {
        try { result.records.push({ type: record.recordType, value: decoder.decode(record.data) }); } catch {}
      }
    } catch {}
  }
  return result;
};

// ─── Buyer Tap to Pay: NFC scan → Stripe form ────────────────────────────
const BuyerNFCPayment = ({ transactionId, pal, T, onPayment, chat, buyerInfo, currency = 'USD' }) => {
  const fmt = (n) => formatShopPrice(n, currency);
  const [showForm, setShowForm]   = useState(false);
  const [nfcStatus, setNfcStatus] = useState('idle'); // idle | scanning | ready | error | unsupported
  const [nfcData, setNfcData]     = useState(null);
  const readerRef = useRef(null);

  useEffect(() => () => { try { readerRef.current?.abort?.(); } catch {} }, []);

  const tryNfc = async () => {
    if (!('NDEFReader' in window)) {
      setNfcStatus('unsupported');
      setShowForm(true);
      return;
    }
    try {
      setNfcStatus('scanning');
      const reader = new window.NDEFReader();
      readerRef.current = reader;
      await reader.scan();
      reader.onreading = (event) => {
        const parsed = parseNfcEvent(event);
        setNfcData(parsed);
        setNfcStatus('ready');
        setShowForm(true);
      };
      reader.onerror = () => {
        setNfcStatus('error');
        setShowForm(true);
      };
    } catch {
      setNfcStatus('error');
      setShowForm(true);
    }
  };

  const statusMsg = nfcStatus === 'ready'
    ? `✅ Card detected${nfcData?.serialNumber ? ` · ID …${nfcData.serialNumber.slice(-4)}` : ''} — complete payment below`
    : nfcStatus === 'unsupported' ? 'NFC not available on this device — enter card manually'
    : 'Enter card details to pay';

  return (
    <AZ>
      {!showForm && (
        <>
          <Btn $v="primary" $a={pal.a} onClick={tryNfc} disabled={nfcStatus === 'scanning'}>
            <Wifi size={14}/>
            {nfcStatus === 'scanning' ? 'Hold card to phone…' : `Tap Card to Pay ${fmt(T.total)}`}
          </Btn>
          {(nfcStatus === 'unsupported' || nfcStatus === 'error') && (
            <Btn $v="ghost" $a={pal.a} onClick={() => setShowForm(true)} style={{ marginTop: '0.4rem' }}>
              <CreditCard size={13}/> Enter Card Manually
            </Btn>
          )}
        </>
      )}
      <ExpandSection $o={showForm}>
        <div style={{ padding: '0.2rem 0 0.5rem' }}>
          <div style={{ fontSize: '0.72rem', opacity: 0.6, marginBottom: '0.5rem', paddingLeft: '0.2rem' }}>
            {statusMsg}
          </div>
          <StripePaymentForm
            amount={T.total} currency={currency} quantity={T.cnt} itemName={`${T.cnt} items`}
            onSuccess={(code, qr) => { setShowForm(false); onPayment(code, qr); }}
            onCancel={() => setShowForm(false)}
            transactionId={transactionId}
            costs={{ subtotal: T.sub, transactionFee: 0, salesTax: 0, total: T.total }}
            chatId={chat?.id} chat={chat}
            paymentMethod="tap_card"
            buyerInfo={buyerInfo}
            nfcData={nfcData}
          />
        </div>
      </ExpandSection>
    </AZ>
  );
};

// ─── Card-on-pickup: seller NFC tap + Stripe form ─────────────────────────
const PickupCardCollect = ({ transactionId, pal, T, onPayment, chat, buyerInfo, currency = 'USD' }) => {
  const [showForm, setShowForm]     = useState(false);
  const [nfcStatus, setNfcStatus]   = useState('idle'); // idle | scanning | error | unsupported
  const tryNfc = async () => {
    if (!('NDEFReader' in window)) {
      setNfcStatus('unsupported');
      setShowForm(true);
      return;
    }
    try {
      setNfcStatus('scanning');
      const reader = new window.NDEFReader();
      await reader.scan();
      reader.onreading = () => {
        setNfcStatus('idle');
        setShowForm(true);
      };
      reader.onerror = () => {
        setNfcStatus('error');
        setShowForm(true);
      };
    } catch {
      setNfcStatus('error');
      setShowForm(true);
    }
  };

  return (
    <AZ>
      {!showForm && (
        <>
          <Btn $v="primary" $a={pal.a} onClick={tryNfc}>
            <CreditCard size={14}/>
            {nfcStatus === 'scanning' ? 'Hold card to phone…' : 'Collect Card Payment'}
          </Btn>
          {nfcStatus === 'unsupported' || nfcStatus === 'error' ? (
            <Btn $v="ghost" $a={pal.a} onClick={() => setShowForm(true)} style={{ marginTop: '0.4rem' }}>
              Enter Card Manually
            </Btn>
          ) : null}
        </>
      )}
      <ExpandSection $o={showForm}>
        <div style={{ padding: '0.2rem 0 0.5rem' }}>
          <div style={{ fontSize: '0.72rem', opacity: 0.6, marginBottom: '0.5rem', paddingLeft: '0.2rem' }}>
            {nfcStatus === 'unsupported' ? 'NFC not available — enter card manually' : 'Enter or confirm card details'}
          </div>
          <StripePaymentForm
              amount={T.total} currency={currency} quantity={T.cnt} itemName={`${T.cnt} items`}
              onSuccess={(code, qr) => { setShowForm(false); onPayment(code, qr); }}
              onCancel={() => setShowForm(false)}
              transactionId={transactionId}
              costs={{ subtotal: T.sub, transactionFee: 0, salesTax: 0, total: T.total }}
              chatId={chat?.id} chat={chat}
              paymentMethod="card_on_pickup"
              buyerInfo={buyerInfo}
            />
        </div>
      </ExpandSection>
    </AZ>
  );
};

const PayMethodSelector = styled.div`
  display: flex;
  gap: 6px;
  padding: 0 1rem;
`;

const PayMethodTab = styled.button`
  flex: 1;
  padding: 9px 6px 7px;
  border-radius: 9px;
  border: 1.5px solid ${p => p.$active ? p.$a : 'rgba(255,255,255,0.1)'};
  background: ${p => p.$active ? `${p.$a}1a` : 'transparent'};
  color: ${p => p.$active ? p.$a : 'rgba(255,255,255,0.45)'};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 5px;
  transition: border-color 0.15s, background 0.15s, color 0.15s;
  &:hover { border-color: ${p => p.$a}60; color: ${p => p.$a}cc; }
`;

// ─── pickup payment options (radio list) ──────────────────────────────────────
const PayOptionsList = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const PayOptionRow = styled.label`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  padding: 0.45rem 0.65rem;
  border-radius: 10px;
  border: 1px solid ${({ $active, $a }) => $active ? $a : 'rgba(255,255,255,0.08)'};
  background: ${({ $active, $a }) => $active ? `${$a}12` : 'rgba(255,255,255,0.02)'};
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;

  input { accent-color: ${({ $a }) => $a}; width: 13px; height: 13px; flex-shrink: 0; }
  .name { font-size: 0.72rem; font-weight: 700; color: #fff; white-space: nowrap; }
`;

// ─── Payment method picker (card / NFC / wallet) ──────────────────────────────
// Detect Apple vs Google wallet brand from user agent
const detectWalletBrand = () => {
  const ua = navigator.userAgent;
  const isApple = /iPhone|iPad|iPod/.test(ua) ||
    (/Mac/.test(ua) && /Safari/.test(ua) && !/Chrome/.test(ua));
  return isApple ? 'apple' : 'google';
};

const ApplePayIcon = () => (
  <svg width="20" height="14" viewBox="0 0 20 14" fill="currentColor">
    <path d="M10.24 2.27c-.5.59-1.31 1.05-2.1.98-.1-.8.29-1.65.75-2.18C9.4.48 10.27.05 10.97 0c.09.83-.24 1.65-.73 2.27zm.72 1.14c-1.16-.07-2.15.66-2.7.66-.56 0-1.4-.63-2.32-.61-1.19.02-2.3.69-2.9 1.77-1.25 2.15-.33 5.33.88 7.08.59.86 1.3 1.81 2.23 1.78.88-.04 1.22-.57 2.29-.57 1.07 0 1.37.57 2.3.55.96-.02 1.57-.87 2.16-1.73.68-.99.96-1.96 .98-2.01-.02-.01-1.88-.72-1.9-2.85-.02-1.78 1.46-2.64 1.53-2.69-.84-1.24-2.14-1.38-2.55-1.38z"/>
  </svg>
);

const GooglePayIcon = () => (
  <svg width="28" height="12" viewBox="0 0 56 24" fill="none">
    <path d="M26.5 12c0 3.87-3.13 7-7 7s-7-3.13-7-7 3.13-7 7-7c1.93 0 3.56.71 4.81 1.86L22.1 9.07C21.27 8.3 20.21 7.85 19.5 7.85c-2.3 0-4.15 1.9-4.15 4.15s1.85 4.15 4.15 4.15c2.65 0 3.64-1.9 3.8-2.9h-3.8v-2.1h6.27c.06.35.09.68.09 1.02-.01-.07-.36 0-.36 0z" fill="#4285F4"/>
    <path d="M19.5 5c1.93 0 3.56.71 4.81 1.86l-2.21 2.21C21.27 8.3 20.21 7.85 19.5 7.85c-2.3 0-4.15 1.9-4.15 4.15s1.85 4.15 4.15 4.15c2.65 0 3.64-1.9 3.8-2.9h-3.8v-2.1h6.27c.06.35.09.68.09 1.02C25.65 15.78 22.94 19 19.5 19c-3.87 0-7-3.13-7-7s3.13-7 7-7z" fill="#4285F4"/>
    <text x="28" y="17" fontFamily="Arial" fontWeight="500" fontSize="13" fill="#4285F4">G</text>
    <text x="35" y="17" fontFamily="Arial" fontWeight="500" fontSize="13" fill="#EA4335">o</text>
    <text x="42" y="17" fontFamily="Arial" fontWeight="500" fontSize="13" fill="#FBBC05">o</text>
  </svg>
);

const PaymentMethodPicker = ({
  pal, T, transactionId, chat, buyerInfo, onPayment, currency = 'USD',
  payMethodChoice, setPayMethodChoice, showPay, setShowPay, isSelfOrder,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [walletBrand, setWalletBrand] = useState('google');
  const [cashConfirming, setCashConfirming] = useState(false);
  const fmt = (n) => formatShopPrice(n, currency);

  useEffect(() => {
    const check = () => setIsMobile(
      window.innerWidth <= 768 || ('ontouchstart' in window) || navigator.maxTouchPoints > 0
    );
    check();
    setWalletBrand(detectWalletBrand());
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleSelect = (method) => {
    setPayMethodChoice(method);
    setShowPay(true);
  };

  const handleCashConfirm = async () => {
    setCashConfirming(true);
    try {
      const chatId = chat?.id || chat?.chatId || transactionId;
      await updateDoc(doc(db, 'transactions', transactionId), {
        paymentMethod: 'cash_on_pickup',
        status: 'paid',
        paymentStatus: 'cash_on_pickup',
        updatedAt: serverTimestamp(),
      });
      if (chatId) {
        await updateDoc(doc(db, 'chats', chatId), {
          status: 'paid',
          transactionStatus: 'paid',
          paymentMethod: 'cash_on_pickup',
          lastMessage: 'Buyer will pay cash at pickup',
          lastMessageTime: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
      onPayment?.(null, null);
    } catch (e) {
      console.error('Cash confirm failed:', e);
    } finally {
      setCashConfirming(false);
    }
  };

  return (
    <AZ>
      <PayMethodSelector>
        <PayMethodTab $active={payMethodChoice === 'card'} $a={pal.a} onClick={() => handleSelect('card')}>
          <CreditCard size={14} />
          Card
        </PayMethodTab>
        <PayMethodTab $active={payMethodChoice === 'cash'} $a={pal.a} onClick={() => handleSelect('cash')}>
          <Banknote size={14} />
          Cash
        </PayMethodTab>
        {isMobile && (
          <PayMethodTab $active={payMethodChoice === 'nfc'} $a={pal.a} onClick={() => handleSelect('nfc')}>
            <Wifi size={14} />
            Tap Card
          </PayMethodTab>
        )}
        {isMobile && (
          <PayMethodTab $active={payMethodChoice === 'wallet'} $a={pal.a} onClick={() => handleSelect('wallet')}>
            {walletBrand === 'apple' ? <ApplePayIcon /> : <GooglePayIcon />}
            {walletBrand === 'apple' ? 'Apple Pay' : 'Google Pay'}
          </PayMethodTab>
        )}
      </PayMethodSelector>

      <ExpandSection $o={showPay}>
        <div style={{ padding: '0.2rem 0 0.5rem' }}>
          {payMethodChoice === 'cash' ? (
            <div style={{ padding: '0.5rem 0' }}>
              <div style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.09)',
                borderRadius: 10,
                padding: '0.75rem 1rem',
                marginBottom: '0.75rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '0.6rem',
              }}>
                <Banknote size={16} style={{ color: pal.a, flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.5 }}>
                  You'll bring <strong style={{ color: 'rgba(255,255,255,0.95)' }}>{fmt(T.total)} cash</strong> and pay the seller directly at pickup. The seller will confirm receipt.
                </div>
              </div>
              <Btn $v="primary" $a={pal.a} onClick={handleCashConfirm} disabled={cashConfirming}>
                {cashConfirming ? '…' : <><Check size={14} /> Confirm Cash Payment</>}
              </Btn>
            </div>
          ) : payMethodChoice === 'nfc' ? (
            <BuyerNFCPayment
              transactionId={transactionId} pal={pal} currency={currency}
              T={T} onPayment={onPayment} chat={chat} buyerInfo={buyerInfo}
            />
          ) : (
            <StripePaymentForm
              amount={T.total} currency={currency} quantity={T.cnt} itemName={`${T.cnt} items`}
              mode={payMethodChoice === 'wallet' ? 'wallet' : 'card'}
              onSuccess={(code, qr) => { setShowPay(false); onPayment(code, qr); }}
              onCancel={() => setShowPay(false)}
              transactionId={transactionId}
              costs={{ subtotal: T.sub, transactionFee: 0, salesTax: 0, total: T.total }}
              chatId={chat?.id} chat={chat}
              buyerInfo={buyerInfo}
            />
          )}
        </div>
      </ExpandSection>
    </AZ>
  );
};

// ─── Service-specific styled components ───────────────────────────────────────
const ServiceInfoCard = styled.div`
  margin: 0 1rem 0.1rem;
  border-radius: 10px;
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.07);
  overflow: hidden;
  animation: ${fadeUp} 0.22s ease both;

  .row {
    display: flex;
    align-items: flex-start;
    gap: 0.55rem;
    padding: 0.52rem 0.75rem;
    border-bottom: 1px solid rgba(255,255,255,0.04);
    &:last-child { border-bottom: none; }
    .icon { color: ${({ $a }) => $a}; flex-shrink: 0; margin-top: 1px; }
    .lines { flex: 1; min-width: 0; }
    .label { font-size: 0.6rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.28); margin-bottom: 2px; }
    .value { font-size: 0.8rem; font-weight: 600; color: rgba(255,255,255,0.82); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  }
`;

const RouteArrow = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 0.42rem 0.75rem;
  background: rgba(255,255,255,0.015);
  .dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
  .line { flex: 1; height: 1px; background: rgba(255,255,255,0.1); }
  .addr { font-size: 0.78rem; color: rgba(255,255,255,0.72); font-weight: 600; flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const ReminderBanner = styled.div`
  margin: 0.5rem 1rem 0;
  border-radius: 9px;
  padding: 0.6rem 0.75rem;
  display: flex; align-items: flex-start; gap: 0.5rem;
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.25);
  animation: ${fadeUp} 0.22s ease both;

  .bell { color: #FBBF24; flex-shrink: 0; margin-top: 1px; }
  .body { font-size: 0.78rem; color: rgba(255,255,255,0.72); line-height: 1.45; }
  .hi   { font-weight: 700; color: #FDE68A; }
`;

const ProgressTrack = styled.div`
  margin: 0.5rem 1rem 0;
  border-radius: 10px;
  border: 1px solid rgba(255,255,255,0.06);
  overflow: hidden;
  animation: ${fadeUp} 0.22s ease both;
`;

const ProgressStep = styled.div`
  display: flex; align-items: center; gap: 0.6rem;
  padding: 0.58rem 0.85rem;
  border-bottom: 1px solid rgba(255,255,255,0.04);
  &:last-child { border-bottom: none; }
  background: ${({ $active, $done }) =>
    $active ? 'rgba(255,255,255,0.05)' : $done ? 'rgba(74,222,128,0.04)' : 'transparent'};

  .dot {
    width: 10px; height: 10px; border-radius: 50%; flex-shrink: 0;
    background: ${({ $active, $done, $a }) =>
      $done   ? '#4ADE80' :
      $active ? $a :
      'rgba(255,255,255,0.1)'};
    box-shadow: ${({ $active, $a, $done }) =>
      $done   ? '0 0 6px rgba(74,222,128,0.6)' :
      $active ? `0 0 8px ${$a}88` :
      'none'};
    ${({ $active }) => $active && css`animation: ${blink} 1.2s step-end infinite;`}
  }
  .lbl {
    font-size: 0.78rem; font-weight: ${({ $active, $done }) => ($active || $done) ? 700 : 500};
    color: ${({ $active, $done, $a }) =>
      $done   ? '#4ADE80' :
      $active ? $a :
      'rgba(255,255,255,0.28)'};
    flex: 1;
  }
  .time { font-size: 0.64rem; color: rgba(255,255,255,0.25); }
`;

const TripSummaryCard = styled.div`
  margin: 0.6rem 1rem 0;
  border-radius: 10px;
  padding: 0.82rem 0.9rem;
  background: rgba(74,222,128,0.06);
  border: 1px solid rgba(74,222,128,0.18);
  animation: ${fadeUp} 0.28s ease both;

  .title { font-size: 0.84rem; font-weight: 700; color: #4ADE80; margin-bottom: 0.5rem; display: flex; align-items: center; gap: 6px; }
  .row   { display: flex; justify-content: space-between; padding: 0.2rem 0; font-size: 0.78rem; }
  .lbl   { color: rgba(255,255,255,0.4); }
  .val   { font-weight: 700; color: rgba(255,255,255,0.82); font-variant-numeric: tabular-nums; }
  .divider { height: 1px; background: rgba(74,222,128,0.15); margin: 0.4rem 0; }
  .total-row { display: flex; justify-content: space-between; padding: 0.25rem 0; }
  .total-lbl { font-size: 0.8rem; font-weight: 700; color: rgba(255,255,255,0.6); }
  .total-val { font-size: 1.1rem; font-weight: 800; color: #4ADE80; font-variant-numeric: tabular-nums; font-family: 'SF Mono', monospace; }
`;

const ServiceActionBtn = styled.button`
  width: calc(100% - 2rem);
  margin: 0.55rem 1rem;
  padding: 0.78rem 1.1rem;
  border-radius: 10px;
  border: none;
  font-size: 0.88rem; font-weight: 700; letter-spacing: 0.02em;
  cursor: pointer;
  display: flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: all 0.18s;

  background: ${({ $color }) => $color || '#A78BFA'};
  color: #000;
  box-shadow: 0 4px 16px ${({ $color }) => ($color || '#A78BFA') + '44'};

  &:hover:not(:disabled) {
    filter: brightness(1.1);
    transform: translateY(-1px);
  }
  &:disabled {
    opacity: 0.4; cursor: not-allowed; transform: none;
  }
`;

// ─── Maps URL builder ─────────────────────────────────────────────────────────
const buildMapsUrl = (fromCoords, fromAddr, toCoords, toAddr) => {
  const enc  = encodeURIComponent;
  const from = fromCoords?.lat ? `${fromCoords.lat},${fromCoords.lng}` : enc(fromAddr || '');
  const to   = toCoords?.lat   ? `${toCoords.lat},${toCoords.lng}`     : enc(toAddr   || '');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return isIOS
    ? `maps://maps.apple.com/?saddr=${from}&daddr=${to}&dirflg=d`
    : `https://www.google.com/maps/dir/?api=1&origin=${from}&destination=${to}&travelmode=driving`;
};
const buildSingleMapsUrl = (coords, addr) => {
  const enc = encodeURIComponent;
  const q   = coords?.lat ? `${coords.lat},${coords.lng}` : enc(addr || '');
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  return isIOS
    ? `maps://maps.apple.com/?q=${q}`
    : `https://www.google.com/maps/search/?api=1&query=${q}`;
};

// ─── image zoom overlay ─────────────────────────────────────────────────────
const ZoomOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: rgba(0,0,0,0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1.5rem;
  cursor: zoom-out;
  animation: ${({ $closing }) => $closing ? zoomBgOut : zoomBgIn} 0.22s ease both;
`;

const ZoomImg = styled.img`
  max-width: 92vw;
  max-height: 88vh;
  border-radius: 14px;
  box-shadow: 0 30px 80px rgba(0,0,0,0.6);
  cursor: default;
  animation: ${({ $closing }) => $closing ? zoomImgOut : zoomImgIn} 0.25s cubic-bezier(0.22,1,0.36,1) both;
`;

const ZoomCloseBtn = styled.button`
  position: absolute;
  top: 1.1rem;
  right: 1.1rem;
  width: 36px; height: 36px;
  border-radius: 50%;
  background: rgba(255,255,255,0.1);
  border: 1px solid rgba(255,255,255,0.18);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: rgba(255,255,255,0.2); }
`;

// ═════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═════════════════════════════════════════════════════════════════════════════
const UnifiedCartWindow = ({
  transaction, chat, currentUser,
  onSendCart, onRespond, onPayment, onServiceProgress, loading,
}) => {
  const navigate = useNavigate();
  const { userLocation, requestLocation } = useUserLocation();
  const [qty,              setQty]              = useState({});
  const [showPay,          setShowPay]          = useState(true);
  const [payMethodChoice,  setPayMethodChoice]  = useState('card');
  const [selfCompleting,   setSelfCompleting]   = useState(false);
  const [showRounds,       setShowRounds]       = useState(false);
  const [showRideReminder, setShowRideReminder] = useState(false);
  const [svcLoading,       setSvcLoading]       = useState(false);
  const [loadedImgs,       setLoadedImgs]       = useState(() => new Set());
  const markImgLoaded = (id) => setLoadedImgs(prev => (prev.has(id) ? prev : new Set(prev).add(id)));
  const [imgAspect,        setImgAspect]        = useState({});
  const handleImgLoad = (id, e) => {
    markImgLoaded(id);
    const { naturalWidth: w, naturalHeight: h } = e.target;
    if (w && h) {
      // Respect the photo's real proportions, clamped so a very tall/wide
      // shot doesn't blow out the row's height.
      const ratio = Math.max(0.75, Math.min(16 / 9, w / h));
      setImgAspect(prev => (prev[id] === ratio ? prev : { ...prev, [id]: ratio }));
    }
  };
  const [collapsed,        setCollapsed]        = useState(false);
  const [collapseSnapshot, setCollapseSnapshot]  = useState(null);
  const toggleCollapsed = () => setCollapsed(c => !c);
  const [zoomImage,        setZoomImage]        = useState(null);
  const [zoomClosing,      setZoomClosing]      = useState(false);
  const openZoom = (src) => { if (src) { setZoomImage(src); setZoomClosing(false); } };
  const closeZoom = () => {
    setZoomClosing(true);
    setTimeout(() => { setZoomImage(null); setZoomClosing(false); }, 200);
  };

  const status          = transaction?.status || chat?.status || 'pending_send';
  const isCollapsed      = collapsed;
  const currencyCode    = transaction?.currency || chat?.currency || chat?.pendingPurchase?.currency || 'USD';
  const fmt             = (n) => formatShopPrice(n, currencyCode);

  // Auto-minimize once an order completes — but only the first time it does,
  // so the user can freely re-expand/re-collapse afterwards without being
  // fought. Resets so the *next* round's completion auto-collapses again too.
  const autoCollapsedRef = useRef(false);
  useEffect(() => {
    if (status === 'completed' && !autoCollapsedRef.current) {
      autoCollapsedRef.current = true;
      setCollapsed(true);
    }
    if (status !== 'completed') autoCollapsedRef.current = false;
  }, [status]);
  const items = useMemo(() => {
    const rawCartItems = chat?.cartItems || transaction?.cartItems;
    if (rawCartItems?.length > 0) return rawCartItems;
    const src = transaction || chat?.pendingPurchase;
    if (!src?.unitPrice) return [];
    return [{
      itemId:            src.itemId || 'single',
      itemName:          src.itemName || '',
      itemImage:         src.itemImage || null,
      unitPrice:         parseFloat(src.unitPrice) || 0,
      quantity:          src.approvedQuantity || src.requestedQuantity || 1,
      availableQuantity: src.availableQuantity || 999,
      // Selected attributes/options/instructions live on the transaction doc for
      // single-item orders (there's no per-item array to carry them on).
      customization:     src.customization || null,
      isService:         !!src.isService,
    }];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    chat?.cartItems,
    transaction?.cartItems,
    transaction?.itemId, transaction?.unitPrice,
    transaction?.approvedQuantity, transaction?.requestedQuantity,
    transaction?.customization,
    chat?.pendingPurchase?.unitPrice, chat?.pendingPurchase?.requestedQuantity,
  ]);
  const paidRounds      = chat?.paidRounds || transaction?.paidRounds || [];
  const pal             = pa(status);
  const isSeller        = currentUser?.role === 'seller';
  const isBuyer         = !isSeller;
  const isSelfOrder     = !!(chat?.isSelfOrder || transaction?.isSelfOrder);
  const paymentMethod   = transaction?.paymentMethod || chat?.paymentMethod || 'card';
  const isPickupPayment = paymentMethod === 'cash_on_pickup' || paymentMethod === 'card_on_pickup';
  const rawItemPickupAddress = transaction?.itemAddress || chat?.itemAddress || null;
  const itemPickupCoords  = transaction?.itemCoordinates || chat?.itemCoordinates || null;

  // ── Pickup payment options: seller's Zelle/Venmo handles + the buyer's choice ─
  const sellerIdForHandles = chat?.sellerId || transaction?.sellerId || null;
  const [sellerPaymentHandles, setSellerPaymentHandles] = useState(null);
  const [sellerShopPic, setSellerShopPic] = useState(null);
  // Live catalog items/services — needed to know each order item's selectable
  // attributes/options/booking fields so they can be edited from the order card.
  const [sellerCatalog, setSellerCatalog] = useState({ items: [], services: [] });
  const [catalogLoaded, setCatalogLoaded] = useState(false);
  useEffect(() => {
    if (!sellerIdForHandles) return;
    let cancelled = false;
    getDoc(doc(db, 'shops', sellerIdForHandles)).then(snap => {
      if (cancelled || !snap.exists()) return;
      const d = snap.data();
      setSellerPaymentHandles(d.paymentHandles || null);
      setSellerShopPic(typeof d.profile === 'string' ? d.profile : d.profile?.image || null);
      setSellerCatalog({ items: d.items || [], services: d.services || [] });
    }).catch(() => {}).finally(() => { if (!cancelled) setCatalogLoaded(true); });
    return () => { cancelled = true; };
  }, [sellerIdForHandles]);
  const getCatalogItem = (itemId) =>
    sellerCatalog.items.find(i => i.id === itemId) || sellerCatalog.services.find(s => s.id === itemId) || null;

  const [buyerPaymentChoice, setBuyerPaymentChoice] = useState(transaction?.buyerPaymentChoice || chat?.buyerPaymentChoice || null);
  useEffect(() => {
    setBuyerPaymentChoice(transaction?.buyerPaymentChoice || chat?.buyerPaymentChoice || null);
  }, [transaction?.buyerPaymentChoice, chat?.buyerPaymentChoice]);

  const handleChoosePaymentMethod = async (method) => {
    setBuyerPaymentChoice(method); // optimistic
    try {
      await TransactionService.chooseBuyerPaymentMethod(transactionId, chat?.id || chat?.chatId, method);
    } catch (e) {
      console.error('Failed to save payment method choice:', e);
    }
  };

  // ── Service item detection ─────────────────────────────────────────────────
  const jobType         = transaction?.jobType || null;
  const serviceTab      = transaction?.serviceTab || null;
  const serviceProgress = transaction?.serviceProgress || null;
  const isServiceItem   = !!(transaction?.isService && jobType);
  const isRide          = jobType === 'rides';
  const isMoving        = jobType === 'moving';
  const isDelivery      = jobType === 'delivery';
  // For 'need' tab jobs the buyer IS the performer (driver/worker who claimed the job)
  const isNeedTab       = serviceTab === 'need';
  const isPerformer     = isServiceItem && (isNeedTab ? isBuyer : isSeller);
  // isClaim: driver/worker claimed a job card — roles are flipped (driver=seller, dispatch=buyer)
  const isClaim         = !!(transaction?.isClaim);

  // Ride / location fields stored on transaction
  const rideDateTime           = transaction?.rideDateTime           || null;
  const rawPickupAddress       = transaction?.pickupAddress          || null;
  const rawDestinationAddress  = transaction?.destinationAddress     || null;
  const passengers             = transaction?.passengers             || null;
  const estimatedDistance      = transaction?.estimatedDistance      || null;
  const vehicleType            = transaction?.vehicleType            || null;
  const tripSummary            = transaction?.tripSummary            || null;
  const pickupCoordinates      = transaction?.pickupCoordinates      || null;
  const destinationCoordinates = transaction?.destinationCoordinates || null;


  // Resolve coordinate strings → human-readable addresses via Nominatim
  const [pickupAddress,      setPickupAddress]      = useState(rawPickupAddress);
  const [destinationAddress, setDestinationAddress] = useState(rawDestinationAddress);

  const isCoordString = s => !!s && /^-?\d+(\.\d+)?,\s*-?\d+(\.\d+)?$/.test(s.trim());

  const reverseGeocode = async (lat, lng) => {
    try {
      const r = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&addressdetails=1`,
        { headers: { 'User-Agent': 'KalKode-Shop-App' } }
      );
      if (!r.ok) return null;
      const d = await r.json();
      const a = d.address || {};
      const street   = [a.house_number, a.road].filter(Boolean).join(' ');
      const locality = a.city || a.town || a.village || a.neighbourhood || a.county;
      const region   = a.state && a.postcode ? `${a.state} ${a.postcode}` : (a.state || a.postcode);
      const parts    = [street, locality, region].filter(Boolean);
      return parts.length ? parts.join(', ') : d.display_name || null;
    } catch { return null; }
  };

  useEffect(() => { setPickupAddress(rawPickupAddress); }, [rawPickupAddress]);
  useEffect(() => { setDestinationAddress(rawDestinationAddress); }, [rawDestinationAddress]);

  useEffect(() => {
    if (!isCoordString(rawPickupAddress)) return;
    const coords = pickupCoordinates || (() => {
      const [lat, lng] = rawPickupAddress.split(',').map(Number);
      return { lat, lng };
    })();
    reverseGeocode(coords.lat, coords.lng).then(a => { if (a) setPickupAddress(a); });
  }, [rawPickupAddress, pickupCoordinates?.lat]);

  // Same coordinate → address resolution for a plain item's pickup location
  const [itemPickupAddress, setItemPickupAddress] = useState(rawItemPickupAddress);
  useEffect(() => { setItemPickupAddress(rawItemPickupAddress); }, [rawItemPickupAddress]);
  useEffect(() => {
    if (!isCoordString(rawItemPickupAddress)) return;
    const coords = itemPickupCoords || (() => {
      const [lat, lng] = rawItemPickupAddress.split(',').map(Number);
      return { lat, lng };
    })();
    reverseGeocode(coords.lat, coords.lng).then(a => { if (a) setItemPickupAddress(a); });
  }, [rawItemPickupAddress, itemPickupCoords?.lat]);

  useEffect(() => {
    if (!isCoordString(rawDestinationAddress)) return;
    const coords = destinationCoordinates || (() => {
      const [lat, lng] = rawDestinationAddress.split(',').map(Number);
      return { lat, lng };
    })();
    reverseGeocode(coords.lat, coords.lng).then(a => { if (a) setDestinationAddress(a); });
  }, [rawDestinationAddress, destinationCoordinates?.lat]);
  const transactionId   = transaction?.id || chat?.transactionId;
  const orderDateLabel  = (() => {
    const raw = transaction?.createdAt || chat?.createdAt;
    try {
      const d = raw?.toDate ? raw.toDate() : (raw ? new Date(raw) : null);
      return d && !isNaN(d) ? d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }) : null;
    } catch { return null; }
  })();

  // Auto-complete self-orders once payment is confirmed (status reaches 'paid')
  useEffect(() => {
    if (!isSelfOrder || status !== 'paid' || selfCompleting) return;
    setSelfCompleting(true);
    TransactionService.completeSelfOrder(transactionId)
      .then(() => onPayment?.(null, null))
      .catch(e => { console.error('Self-order completion failed:', e); setSelfCompleting(false); });
  }, [isSelfOrder, status, transactionId]);

  // Build buyerInfo for StripePaymentForm — the buyerId the API validates against
  const buyerInfo = {
    uid: chat?.buyerId || transaction?.buyerId || currentUser?.id,
    name: currentUser?.displayName || currentUser?.name || 'Buyer',
    userType: (chat?.isGuestOrder || transaction?.isGuestOrder) ? 'guest' : 'authenticated',
  };


  // 1-hour ride reminder — checks every 60 s
  useEffect(() => {
    if (!isRide || !rideDateTime) return;
    const check = () => {
      const diff = new Date(rideDateTime) - Date.now();
      setShowRideReminder(diff > 0 && diff <= 3_600_000);
    };
    check();
    const id = setInterval(check, 60_000);
    return () => clearInterval(id);
  }, [isRide, rideDateTime]);

  // init quantities
  useEffect(() => {
    if (!items.length) return;
    const init = {};
    items.forEach(i => { init[i.itemId || i.id] = i.quantity || 1; });
    setQty(init);
  }, [items]);

  // totals
  const T = (() => {
    let sub = 0, cnt = 0;
    items.forEach(i => {
      const q = qty[i.itemId || i.id] || i.quantity || 1;
      sub += parseFloat(i.unitPrice || i.price || 0) * q;
      cnt += q;
    });
    const fee    = sub * 0.07;
    const sfee   = (sub * 0.029) + 0.30;
    const plat   = Math.max(0, fee - sfee);
    return { sub, total: sub, cnt, fee, sfee, plat, payout: sub - fee };
  })();

  // Snapshot what the order looked like the moment it was minimized, so we can
  // tell the user something changed underneath while it's still collapsed.
  useEffect(() => {
    if (collapsed) {
      setCollapseSnapshot({ status, itemCount: items.length, total: T.total, buyerPaymentChoice, serviceProgress });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  const hasChangesWhileCollapsed = collapsed && !!collapseSnapshot && (
    collapseSnapshot.status !== status ||
    collapseSnapshot.itemCount !== items.length ||
    collapseSnapshot.total !== T.total ||
    collapseSnapshot.buyerPaymentChoice !== buyerPaymentChoice ||
    collapseSnapshot.serviceProgress !== serviceProgress
  );

  const changeQty = async (id, n) => {
    const item = items.find(i => (i.itemId || i.id) === id);
    if (!item) return;
    const prevQty = qty[id] || item.quantity || 1;
    const newQty = Math.max(1, Math.min(n, item.availableQuantity || 999));
    setQty(p => ({ ...p, [id]: newQty }));

    // Buyer edits persist immediately while the order is still editable (same
    // window as canRemoveItems below); the seller's pre-accept adjustment stays
    // local and is committed via onRespond('accept', ...).
    if (isBuyer && ['pending_send', 'pending_seller_acceptance', 'seller_accepted'].includes(status)) {
      const cId = chat?.id || chat?.chatId || transactionId;
      if (!cId) return;
      try {
        await TransactionService.updateItemQuantity(cId, id, newQty);
      } catch (e) {
        console.error('Failed to update item quantity:', e);
        setQty(p => ({ ...p, [id]: prevQty }));
        alert('Could not update quantity: ' + (e.message || 'Unknown error'));
      }
    }
  };

  // Buyer can drop an item while the order's still editable — before it's
  // been paid for, and only if it isn't the last one left.
  const canRemoveItems = isBuyer && items.length > 1
    && ['pending_send', 'pending_seller_acceptance', 'seller_accepted'].includes(status);
  const [removingId, setRemovingId] = useState(null);
  const handleRemoveItem = async (itemId) => {
    const cId = chat?.id || chat?.chatId || transactionId;
    if (!cId || removingId) return;
    setRemovingId(itemId);
    try {
      await TransactionService.removeItemFromOrder(cId, itemId);
    } catch (e) {
      alert('Could not remove item: ' + (e.message || 'Unknown error'));
    } finally {
      setRemovingId(null);
    }
  };

  // Buyer can edit an item's selected options / booking-form answers in the
  // same window they can still edit quantity or drop the item.
  const canEditOptions = isBuyer
    && ['pending_send', 'pending_seller_acceptance', 'seller_accepted'].includes(status);
  const [editingItemId, setEditingItemId] = useState(null);
  const [editValues, setEditValues] = useState({});
  const [savingOptionsId, setSavingOptionsId] = useState(null);
  const [locatingFieldKey, setLocatingFieldKey] = useState(null);
  const [mapPickerFieldKey, setMapPickerFieldKey] = useState(null);

  // GPS icon — reads the browser's current position and reverse-geocodes it
  // straight into the field, no map interaction needed.
  const handleUseCurrentLocation = (fieldKey) => {
    if (!navigator.geolocation) { alert('Geolocation is not supported by your browser'); return; }
    setLocatingFieldKey(fieldKey);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const addr = await reverseGeocode(position.coords.latitude, position.coords.longitude);
        setEditValues(prev => ({
          ...prev,
          [fieldKey]: addr || `${position.coords.latitude.toFixed(6)}, ${position.coords.longitude.toFixed(6)}`,
        }));
        setLocatingFieldKey(null);
      },
      () => { alert('Could not get your location — check location permissions.'); setLocatingFieldKey(null); },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  // Map icon — opens the treasure map in point-selection mode so the buyer can
  // drop a pin instead of typing/GPS-ing an address.
  const handleOpenMapPicker = (fieldKey) => {
    if (!userLocation) requestLocation();
    setMapPickerFieldKey(fieldKey);
  };
  const handleMapLocationPicked = async ({ lat, lng }) => {
    const fieldKey = mapPickerFieldKey;
    setMapPickerFieldKey(null);
    if (!fieldKey) return;
    const addr = await reverseGeocode(lat, lng);
    setEditValues(prev => ({ ...prev, [fieldKey]: addr || `${lat.toFixed(6)}, ${lng.toFixed(6)}` }));
  };

  const openOptionsEditor = (id, initialValues) => {
    setEditingItemId(id);
    setEditValues(initialValues || {});
  };
  const closeOptionsEditor = () => {
    setEditingItemId(null);
    setEditValues({});
  };

  // Auto-open the options editor for a service item on load — a service is
  // never charged a sensible default duration, so surfacing the control
  // immediately (instead of behind a click) means the price shown is right
  // from the start instead of silently wrong until the buyer thinks to look.
  const autoExpandDone = useRef(false);
  useEffect(() => {
    if (autoExpandDone.current || !canEditOptions || !catalogLoaded || items.length === 0) return;
    autoExpandDone.current = true;
    const firstServiceItem = items.find(it => getCatalogItem(it.itemId || it.id)?.isService);
    if (firstServiceItem) {
      const id = firstServiceItem.itemId || firstServiceItem.id;
      openOptionsEditor(id, firstServiceItem.customization?.orderDetails || {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEditOptions, catalogLoaded, items]);

  const saveOptionsEditor = async (itemId) => {
    const cId = chat?.id || chat?.chatId || transactionId;
    if (!cId) return;
    setSavingOptionsId(itemId);
    try {
      // Hourly/daily services are priced by duration — re-derive the actual
      // charge from the catalog rate so the price follows the edited duration
      // instead of staying stuck at whatever it was booked at.
      const catalogItem = getCatalogItem(itemId);
      let newTotalPrice = null;
      let valuesToSave = editValues;
      if (catalogItem?.isService && catalogItem.pricingType !== 'total') {
        const rate = parseFloat(catalogItem.price) || 0;
        if (catalogItem.pricingType === 'daily') {
          const days = parseInt(editValues.durationDays) || 1;
          newTotalPrice = rate * days;
        } else {
          const hours = parseInt(editValues.durationHours) || 1;
          newTotalPrice = rate * hours;
        }
        valuesToSave = { ...editValues, pricingType: catalogItem.pricingType || 'hourly', totalPrice: newTotalPrice };
      }
      await TransactionService.updateItemCustomization(cId, itemId, valuesToSave, newTotalPrice);
      closeOptionsEditor();
    } catch (e) {
      alert('Could not update options: ' + (e.message || 'Unknown error'));
    } finally {
      setSavingOptionsId(null);
    }
  };

  // Renders a single order-form field (chip-select / text / textarea / select /
  // date / stepper / toggle) bound to the in-progress editValues state.
  const renderOptionField = (field) => {
    const value = editValues[field.key];
    const setValue = (v) => setEditValues(prev => ({ ...prev, [field.key]: v }));

    if (field.type === 'chip-select') {
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}{field.required && ' *'}</div>
          <div className="chips">
            {field.options.map(opt => (
              <div
                key={opt}
                className={`chip${value === opt ? ' selected' : ''}`}
                onClick={() => setValue(value === opt ? '' : opt)}
              >
                {opt}
              </div>
            ))}
          </div>
        </div>
      );
    }
    if (field.type === 'textarea') {
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}</div>
          <textarea value={value || ''} placeholder={field.placeholder || ''} onChange={e => setValue(e.target.value)} />
        </div>
      );
    }
    if (field.type === 'select') {
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}{field.required && ' *'}</div>
          <select value={value || ''} onChange={e => setValue(e.target.value)}>
            <option value="">Select…</option>
            {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        </div>
      );
    }
    if (field.type === 'date' || field.type === 'datetime-local') {
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}</div>
          <input type={field.type} value={value || ''} onChange={e => setValue(e.target.value)} />
        </div>
      );
    }
    if (field.type === 'stepper') {
      const n = value != null ? parseInt(value) : (field.defaultVal || field.min || 1);
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}</div>
          <div className="stepper">
            <button type="button" onClick={() => setValue(Math.max(field.min ?? 1, n - 1))}><Minus size={12} /></button>
            <span className="n">{n}</span>
            <button type="button" onClick={() => setValue(Math.min(field.max ?? 999, n + 1))}><Plus size={12} /></button>
          </div>
        </div>
      );
    }
    if (field.type === 'toggle') {
      return (
        <label key={field.key} className="toggle-row">
          <span className="field-label" style={{ marginBottom: 0 }}>{field.label}</span>
          <input type="checkbox" checked={!!value} onChange={e => setValue(e.target.checked)} />
        </label>
      );
    }
    if (LOCATION_FIELD_KEYS.has(field.key)) {
      const isLocating = locatingFieldKey === field.key;
      return (
        <div key={field.key}>
          <div className="field-label">{field.label}{field.required && ' *'}</div>
          <div className="location-row">
            <input type="text" value={value || ''} placeholder={field.placeholder || ''} onChange={e => setValue(e.target.value)} />
            <button type="button" title="Use current location" disabled={isLocating} onClick={() => handleUseCurrentLocation(field.key)}>
              <Navigation size={14} />
            </button>
            <button type="button" title="Pick on map" onClick={() => handleOpenMapPicker(field.key)}>
              <MapPin size={14} />
            </button>
          </div>
        </div>
      );
    }
    return (
      <div key={field.key}>
        <div className="field-label">{field.label}{field.required && ' *'}</div>
        <input type="text" value={value || ''} placeholder={field.placeholder || ''} onChange={e => setValue(e.target.value)} />
      </div>
    );
  };

  // ⚠️ Stripe verification disabled — all orders use the mock payment flow for now.
  const handleAccept = () => {
    const updated = items.map(i => {
      const id = i.itemId || i.id;
      const q  = qty[id] || i.quantity || 1;
      return { itemId:id, itemName:i.itemName||i.name, itemImage:i.itemImage||i.image, unitPrice:parseFloat(i.unitPrice||i.price||0), quantity:parseInt(q), total:parseFloat((i.unitPrice||i.price||0)*q), customization: i.customization || null };
    });
    // TransactionService expects an array only for real multi-item cart orders —
    // for a single-item order it wants the plain approved quantity as a number.
    const isRealCartOrder = (chat?.cartItems?.length > 0) || (transaction?.cartItems?.length > 0);
    if (isRealCartOrder) {
      onRespond('accept', updated);
    } else {
      onRespond('accept', updated[0]?.quantity || 1);
    }
  };

  // ── service progress handler ──────────────────────────────────────────────
  const handleSvcProgress = async (progress) => {
    if (!onServiceProgress || svcLoading) return;
    try {
      setSvcLoading(true);
      await onServiceProgress(transactionId, progress);
    } catch (e) {
      console.error('Service progress error:', e);
      alert(e.message);
    } finally {
      setSvcLoading(false);
    }
  };

  // ── item list renderer ────────────────────────────────────────────────────
  const ItemList = ({ editable = false }) => (
    <>
      {items.length === 0 && (
        <>
          <SkelRow>
            <div className="img-wrap"><div className="img-box"><ShimmerOverlay /></div></div>
            <div className="details-row">
              <div className="body"><div className="bar name" /><div className="bar tag" /></div>
              <div className="price-col"><div className="bar price" /><div className="bar qty" /></div>
            </div>
          </SkelRow>
          <SkelRow>
            <div className="img-wrap"><div className="img-box"><ShimmerOverlay /></div></div>
            <div className="details-row">
              <div className="body"><div className="bar name" /><div className="bar tag" /></div>
              <div className="price-col"><div className="bar price" /><div className="bar qty" /></div>
            </div>
          </SkelRow>
        </>
      )}
      {items.map((item, i) => {
        const id    = item.itemId || item.id || `item-${i}`;
        const q     = qty[id] || item.quantity || 1;
        const up    = parseFloat(item.unitPrice || item.price || 0);
        const tot   = up * q;
        const thumb = item.itemImage || item.imageUrl;
        const isLoaded = loadedImgs.has(id);
        // Services are priced hourly/daily/flat (set on the shop's service editor) —
        // "quantity" isn't a meaningful dimension for them the way it is for a
        // physical product; the bookable amount is duration, edited via the
        // options form below instead. Editing qty here doesn't match how the
        // booking's total was actually computed, so it's product-only.
        const isServiceRow = !!item.isService || sellerCatalog.services.some(s => s.id === id);
        // Selected attributes/custom-option live under customization.orderDetails
        // (attr_* / customOption keys, produced by the order form) for real orders;
        // the flatter selectedAttributes/option shape is kept as a legacy fallback.
        const itemOrderDetails = item.customization?.orderDetails || null;
        const optionLabel = item.customization?.selectedAttributes
          ? Object.values(item.customization.selectedAttributes).join(', ')
          : item.customization?.option
            || (itemOrderDetails
                  ? Object.entries(itemOrderDetails)
                      .filter(([k]) => k.startsWith('attr_') || k === 'customOption')
                      .map(([, v]) => v)
                      .join(', ') || null
                  : null);
        // For an hourly/daily service, show the actual rate × duration (e.g.
        // "$200 × 2 hrs") instead of the misleading "$400 × 1" — up/tot/q stay
        // as the real total/qty=1 used for the order's actual math.
        const svcCatalogItem = isServiceRow ? getCatalogItem(id) : null;
        const svcRate = svcCatalogItem?.isService && svcCatalogItem.pricingType !== 'total'
          ? parseFloat(svcCatalogItem.price) || 0
          : null;
        const svcDuration = itemOrderDetails?.durationDays
          ? { n: itemOrderDetails.durationDays, unit: itemOrderDetails.durationDays === 1 ? 'day' : 'days' }
          : itemOrderDetails?.durationHours
            ? { n: itemOrderDetails.durationHours, unit: itemOrderDetails.durationHours === 1 ? 'hr' : 'hrs' }
            : null;
        return (
          <IRow key={id} $i={i} $a={pal.a}>
            {canRemoveItems && (
              <button
                type="button"
                className="remove-btn"
                title="Remove item"
                disabled={removingId === id}
                onClick={e => { e.stopPropagation(); handleRemoveItem(id); }}
              >
                <Trash2 size={13} />
              </button>
            )}
            <div className="img-wrap">
              {thumb
                ? (
                  <div
                    className="img-box"
                    onClick={() => openZoom(thumb)}
                    style={{ cursor: 'zoom-in', aspectRatio: imgAspect[id] || undefined }}
                  >
                    {!isLoaded && <ShimmerOverlay style={{ zIndex: 2 }} />}
                    <img
                      className={`thumb${isLoaded ? ' is-loaded' : ''}`}
                      src={thumb}
                      alt={item.itemName||item.name}
                      loading="eager"
                      decoding="async"
                      onLoad={e => handleImgLoad(id, e)}
                      onError={e => { e.target.style.display = 'none'; markImgLoaded(id); }}
                    />
                  </div>
                )
                : (
                  <div className="ph">
                    <Package size={22}/>
                  </div>
                )
              }
            </div>
            <div className="details-row">
              <div className="body">
                <div className="name">
                  {item.itemName || item.name || 'Item'}
                  {optionLabel && <span style={{ color:'rgba(255,255,255,0.45)', fontWeight: 500 }}> ({optionLabel})</span>}
                </div>
                {(item.customization?.selectedAttributes || item.customization?.options || item.customization?.option || item.customization?.instructions) && (
                  <div className="tags">
                    {item.customization.selectedAttributes
                      ? Object.entries(item.customization.selectedAttributes).map(([k, v]) => (
                          <CTag key={k} $o>{k}: {v}</CTag>
                        ))
                      : item.customization.options
                        ? Object.entries(item.customization.options).map(([opt, qty]) => (
                            <CTag key={opt} $o>⚙ {opt}: ×{qty}</CTag>
                          ))
                        : item.customization.option && <CTag $o>⚙ {item.customization.option}</CTag>
                    }
                    {item.customization.instructions && (
                      <CTag title={item.customization.instructions}>
                        📝 {item.customization.instructions.length > 30 ? item.customization.instructions.slice(0,30)+'…' : item.customization.instructions}
                      </CTag>
                    )}
                  </div>
                )}
                {(() => {
                  const catalogItem = getCatalogItem(id);
                  const formFields = catalogItem ? getItemFormFields(catalogItem) : [];
                  // Duration drives an hourly/daily service's actual price, so it's a
                  // dedicated, always-present control here rather than just another
                  // entry in the generic booking-form field list.
                  const durationField = catalogItem?.isService && catalogItem.pricingType !== 'total'
                    ? (catalogItem.pricingType === 'daily'
                        ? { key: 'durationDays',  label: 'Duration (days)',  type: 'stepper', min: 1, max: 90, defaultVal: 1 }
                        : { key: 'durationHours', label: 'Duration (hours)', type: 'stepper', min: 1, max: 24, defaultVal: 1 })
                    : null;
                  const hasAnyControls = formFields.length > 0 || !!durationField;
                  const savedEntries = getOrderDetailsEntries(itemOrderDetails);

                  if (!hasAnyControls || !canEditOptions) {
                    // No live field definitions, or past the editable window —
                    // fall back to a read-only display of whatever was saved.
                    return savedEntries.length > 0 ? (
                      <div className="tags">
                        {savedEntries.map(({ key, label, value }) => (
                          <CTag key={key}>{label}: {value}</CTag>
                        ))}
                      </div>
                    ) : null;
                  }

                  const isEditingThis = editingItemId === id;
                  return (
                    <>
                      <OptionsToggleBtn
                        type="button"
                        $a={pal.a}
                        onClick={() => isEditingThis ? closeOptionsEditor() : openOptionsEditor(id, itemOrderDetails || {})}
                      >
                        {isEditingThis ? <ChevronUp /> : <ChevronDown />}
                        {isEditingThis ? 'Hide options' : (savedEntries.length > 0 ? 'Edit options' : 'Add options')}
                      </OptionsToggleBtn>
                      {isEditingThis && (
                        <OptionsEditor $a={pal.a} onClick={e => e.stopPropagation()}>
                          {durationField && renderOptionField(durationField)}
                          {formFields.map(f => renderOptionField(f))}
                          <div className="actions">
                            <div><Btn $sm onClick={closeOptionsEditor} disabled={savingOptionsId === id}>Cancel</Btn></div>
                            <div>
                              <Btn $sm $v="primary" $a={pal.a} onClick={() => saveOptionsEditor(id)} disabled={savingOptionsId === id}>
                                {savingOptionsId === id ? 'Saving…' : 'Save'}
                              </Btn>
                            </div>
                          </div>
                        </OptionsEditor>
                      )}
                    </>
                  );
                })()}
                <div className="unit">
                  {editable && !isServiceRow ? (
                    <div className="qty-ctrl">
                      <button onClick={()=>changeQty(id,q-1)} disabled={q<=1||loading}><Minus/></button>
                      <span className="n">{q}</span>
                      <button onClick={()=>changeQty(id,q+1)} disabled={loading||q>=(item.availableQuantity||999)}><Plus/></button>
                    </div>
                  ) : svcRate != null && svcDuration ? (
                    <>
                      <span>{fmt(svcRate)}</span>
                      <span style={{ color:'rgba(255,255,255,0.18)' }}>×</span>
                      <span style={{ color:'rgba(255,255,255,0.65)', fontWeight:700 }}>{svcDuration.n} {svcDuration.unit}</span>
                    </>
                  ) : (
                    <>
                      <span>{fmt(up)}</span>
                      <span style={{ color:'rgba(255,255,255,0.18)' }}>×</span>
                      <span style={{ color:'rgba(255,255,255,0.65)', fontWeight:700 }}>{q}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="price-col">
                <div className="total">{fmt(tot)}</div>
              </div>
            </div>
          </IRow>
        );
      })}
    </>
  );

  // ── receipt totals ────────────────────────────────────────────────────────
  const shortPickupAddress = itemPickupAddress ? itemPickupAddress.split(',')[0].trim() : null;

  const Summary = ({ showPayout = false }) => (
    <>
      <GrandTotal>
        <div>
          <div className="label">{showPayout ? 'Your Payout' : 'Total'}</div>
        </div>
        <div className="amount" style={{ color: showPayout ? '#4ADE80' : '#fff' }}>
          {fmt(T.total)}
        </div>
      </GrandTotal>
      <TotalLine />
      <Totals>
        <div className="row">
          <span className="l">{(transaction?.isService || chat?.pendingPurchase?.isService) ? 'Office' : 'Pick Up'}</span>
          {shortPickupAddress ? (
            <a
              href={buildSingleMapsUrl(itemPickupCoords, itemPickupAddress)}
              target="_blank"
              rel="noopener noreferrer"
              onClick={e => e.stopPropagation()}
              title={itemPickupAddress}
              className="free"
              style={{
                maxWidth: '68%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                display: 'inline-flex', alignItems: 'center', gap: '4px', textDecoration: 'none',
              }}
            >
              <Navigation size={11} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{shortPickupAddress}</span>
            </a>
          ) : (
            <span className="free">In Person</span>
          )}
        </div>
      </Totals>
      {isBuyer && sellerIdForHandles && (
        <AddMoreItemsBtn
          $a={pal.a}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/shop/${sellerIdForHandles}/view?tab=shop`);
          }}
        >
          <span className="avatar">
            {sellerShopPic ? (
              <img src={sellerShopPic} alt="" onError={(e) => { e.target.style.display = 'none'; }} />
            ) : (
              <Store size={11} />
            )}
          </span>
          Add more items
          <Plus size={13} />
        </AddMoreItemsBtn>
      )}
    </>
  );

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <>
    <Card>
      <Inner>

        {/* ── Header ──────────────────────────────────────────────── */}
        <Strip
          $a={pal.a}
          onClick={toggleCollapsed}
          style={{ cursor: 'pointer' }}
        >
          <div className="left">
            <div className="title">
              {isSeller && status === 'pending_seller_acceptance' ? 'New Order Request'
                : status === 'seller_accepted' && isSeller          ? 'Order Accepted'
                : status === 'paid'   && isSeller                   ? 'Payment Received'
                : status === 'paid'   && isBuyer                    ? 'Payment Confirmed'
                : status === 'completed'                             ? 'Order Complete'
                : isBuyer                                            ? (orderDateLabel ? `Order date: ${orderDateLabel}` : 'Order')
                : 'Order'}
            </div>
            <div className="sub">{items.length} item{items.length!==1?'s':''} · {fmt(T.total)}</div>
          </div>
          <StatusWord $a={pal.a} $live={status==='pending_send'||status==='pending_seller_acceptance'}>
            <span className="live" />
            {pal.word}
          </StatusWord>
          {hasChangesWhileCollapsed && (
            <ChangeDot title="Something changed while minimized" />
          )}
          {isCollapsed
            ? <ChevronDown size={16} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
            : <ChevronUp size={16} style={{ color: 'rgba(255,255,255,0.4)', flexShrink: 0 }} />
          }
        </Strip>

        {!isCollapsed && <>

        {/* ── Paid rounds history ─────────────────────────────────── */}
        {paidRounds.length > 0 && (
          <Receipt>
            <SectionHead $a="#6B7280" style={{ cursor: 'pointer' }} onClick={() => setShowRounds(r => !r)}>
              <span className="label">Previous Paid Orders ({paidRounds.length})</span>
              <span className="badge" style={{ background: 'rgba(107,114,128,0.18)', color: '#6B7280' }}>
                {showRounds ? '▲' : '▼'}
              </span>
            </SectionHead>
            <ExpandSection $o={showRounds}>
              {paidRounds.map((round, ri) => (
                <div key={ri} style={{ padding: '0.5rem 1rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.35)', marginBottom: '0.35rem', fontWeight: 700 }}>
                    Round {round.roundNumber || ri + 1} · {fmt(round.total || 0)} · PAID
                  </div>
                  {(round.items || []).map((item, ii) => (
                    <div key={ii} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.76rem', color: 'rgba(255,255,255,0.4)', padding: '2px 0' }}>
                      <span>{item.quantity}× {item.itemName || item.name || 'Item'}</span>
                      <span>{fmt((item.unitPrice || 0) * item.quantity)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </ExpandSection>
          </Receipt>
        )}

        {/* ── Ride info card (rides / moving / delivery only) ─────── */}
        {isServiceItem && (isRide || isMoving || isDelivery) && (pickupAddress || destinationAddress || rideDateTime) && (
          <ServiceInfoCard $a={pal.a} style={{ marginTop: '0.6rem' }}>
            {isRide && (
              <>
                {(pickupAddress || destinationAddress) && (
                  <>
                    {pickupAddress && (
                      <RouteArrow>
                        <div className="dot" style={{ background: '#4ADE80' }} />
                        <div className="addr">{pickupAddress}</div>
                        <a
                          href={pickupAddress && destinationAddress
                            ? buildMapsUrl(pickupCoordinates, pickupAddress, destinationCoordinates, destinationAddress)
                            : buildSingleMapsUrl(pickupCoordinates, pickupAddress)}
                          target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          title="Navigate"
                          style={{ flexShrink:0, color:'rgba(255,255,255,0.4)', display:'flex', alignItems:'center', textDecoration:'none', padding:'2px' }}
                        >
                          <Navigation size={13} />
                        </a>
                      </RouteArrow>
                    )}
                    {pickupAddress && destinationAddress && (
                      <div style={{ display:'flex', alignItems:'center', padding:'0 0.85rem' }}>
                        <div style={{ width:1, height:14, background:'rgba(255,255,255,0.08)', margin:'0 3px 0 3px' }}/>
                        <ArrowRight size={12} style={{ color:'rgba(255,255,255,0.2)' }}/>
                      </div>
                    )}
                    {destinationAddress && (
                      <RouteArrow>
                        <div className="dot" style={{ background: '#F87171' }} />
                        <div className="addr">{destinationAddress}</div>
                        <a
                          href={pickupAddress && destinationAddress
                            ? buildMapsUrl(pickupCoordinates, pickupAddress, destinationCoordinates, destinationAddress)
                            : buildSingleMapsUrl(destinationCoordinates, destinationAddress)}
                          target="_blank" rel="noopener noreferrer"
                          onClick={e => e.stopPropagation()}
                          title="Navigate"
                          style={{ flexShrink:0, color:'rgba(255,255,255,0.4)', display:'flex', alignItems:'center', textDecoration:'none', padding:'2px' }}
                        >
                          <Navigation size={13} />
                        </a>
                      </RouteArrow>
                    )}
                  </>
                )}
              </>
            )}
            {!isRide && pickupAddress && (
              <div className="row">
                <MapPin size={13} className="icon" />
                <div className="lines">
                  <div className="label">Pickup</div>
                  <div className="value">{pickupAddress}</div>
                </div>
                <a
                  href={pickupAddress && destinationAddress
                    ? buildMapsUrl(pickupCoordinates, pickupAddress, destinationCoordinates, destinationAddress)
                    : buildSingleMapsUrl(pickupCoordinates, pickupAddress)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  title="Navigate"
                  style={{ flexShrink:0, color:'rgba(255,255,255,0.35)', display:'flex', alignItems:'center', textDecoration:'none', padding:'2px', marginTop:'1px' }}
                >
                  <Navigation size={13} />
                </a>
              </div>
            )}
            {!isRide && destinationAddress && (
              <div className="row">
                <Flag size={13} className="icon" />
                <div className="lines">
                  <div className="label">Destination</div>
                  <div className="value">{destinationAddress}</div>
                </div>
                <a
                  href={pickupAddress && destinationAddress
                    ? buildMapsUrl(pickupCoordinates, pickupAddress, destinationCoordinates, destinationAddress)
                    : buildSingleMapsUrl(destinationCoordinates, destinationAddress)}
                  target="_blank" rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  title="Navigate"
                  style={{ flexShrink:0, color:'rgba(255,255,255,0.35)', display:'flex', alignItems:'center', textDecoration:'none', padding:'2px', marginTop:'1px' }}
                >
                  <Navigation size={13} />
                </a>
              </div>
            )}
            {rideDateTime && (
              <div className="row">
                <Clock size={13} className="icon" />
                <div className="lines">
                  <div className="label">Date &amp; Time</div>
                  <div className="value">
                    {(() => {
                      try { return new Date(rideDateTime).toLocaleString([], { dateStyle:'medium', timeStyle:'short' }); }
                      catch { return rideDateTime; }
                    })()}
                  </div>
                </div>
              </div>
            )}
            {passengers && (
              <div className="row">
                <Users size={13} className="icon" />
                <div className="lines">
                  <div className="label">Passengers</div>
                  <div className="value">{passengers}</div>
                </div>
              </div>
            )}
            {vehicleType && (
              <div className="row">
                <Car size={13} className="icon" />
                <div className="lines">
                  <div className="label">Vehicle</div>
                  <div className="value">{vehicleType}</div>
                </div>
              </div>
            )}
            {estimatedDistance && (
              <div className="row">
                <Navigation size={13} className="icon" />
                <div className="lines">
                  <div className="label">Est. Distance</div>
                  <div className="value">{estimatedDistance}</div>
                </div>
              </div>
            )}
          </ServiceInfoCard>
        )}

        {/* ── 1-hour ride reminder banner ──────────────────────────── */}
        {showRideReminder && (status === 'seller_accepted' || status === 'paid') && (
          <ReminderBanner>
            <Bell size={15} className="bell" />
            <div className="body">
              <span className="hi">Pickup in less than 1 hour.</span> Driver should be en route to pickup location.
            </div>
          </ReminderBanner>
        )}

        {/* ── Items — hidden for non-service buyer paid (replaced with compact strip) */}
        {!(status === 'paid' && isBuyer && !isServiceItem) && (
          <Receipt>
            <SectionHead $a={pal.a}>
              <span className="label">Items</span>
              <span className="badge">{items.length}</span>
            </SectionHead>
            <Dash />
            {ItemList({
              editable: (isSeller && status === 'pending_seller_acceptance')
                || (isBuyer && ['pending_send', 'pending_seller_acceptance', 'seller_accepted'].includes(status))
            })}
            <Dash />
          </Receipt>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 1 — pending_send  (buyer)
        ════════════════════════════════════════════════════════ */}
        {status === 'pending_send' && isBuyer && (
          <>
            {Summary({})}
            <StateDivider />
            <StatePanel>
              <InfoLine $a={pal.a}>
                <ShieldCheck size={14} className="ico" />
                <span className="txt">
                  <span className="bold">No charge yet.</span> You won't be billed until the seller accepts and you complete payment.
                </span>
              </InfoLine>
            </StatePanel>
            <AZ>
              <Btn $v="primary" $a={pal.a} onClick={onSendCart} disabled={loading || !items.length}>
                {loading
                  ? <>Sending <Dots><span/><span/><span/></Dots></>
                  : <><Send size={14}/>Send Order to Seller</>}
              </Btn>
            </AZ>
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 2 — pending_seller_acceptance  (SELLER)
        ════════════════════════════════════════════════════════ */}
        {status === 'pending_seller_acceptance' && isSeller && (
          <>
            {Summary({ showPayout: true })}
            {isSelfOrder ? (
              <>
                <StateDivider />
                <StatePanel>
                  <WaitText $a={pal.a}>
                    Auto-accepting your self-order<span className="cursor"/>
                  </WaitText>
                  <InfoLine $a={pal.a} style={{ marginTop:'8px' }}>
                    <Sparkles size={13} className="ico"/>
                    <span className="txt">Your order is being confirmed — payment will appear here shortly.</span>
                  </InfoLine>
                </StatePanel>
              </>
            ) : isClaim ? (
              // Driver/worker claimed this job — waiting for dispatch to confirm
              <StatePanel>
                <WaitText $a={pal.a}>
                  Claim submitted<span className="cursor"/>
                </WaitText>
                <InfoLine $a={pal.a} style={{ marginTop:'8px' }}>
                  <Sparkles size={13} className="ico"/>
                  <span className="txt">Waiting for the dispatcher to confirm your claim. You'll be notified when accepted.</span>
                </InfoLine>
              </StatePanel>
            ) : (
              <>
                <Gap n={0.55} />
                <div style={{ padding:'0 1rem' }}>
                  <MarchBox $a={pal.a}>
                    <PanelTitle $a={pal.a}>Review & Accept</PanelTitle>
                    <InfoLine $a={pal.a}>
                      <CheckCircle size={13} className="ico"/>
                      <span className="txt">Adjust quantities if needed, then accept. Buyer will be prompted to pay.</span>
                    </InfoLine>
                  </MarchBox>
                </div>
                <AZ>
                  <Btn $v="primary" $a={pal.a} onClick={handleAccept} disabled={loading}>
                    {loading
                      ? <>Processing <Dots><span/><span/><span/></Dots></>
                      : <><Check size={14}/>Accept Order · {fmt(T.total)}</>}
                  </Btn>
                  <Btn $v="danger" onClick={()=>onRespond('reject')} disabled={loading}>
                    <X size={13}/>Decline Order
                  </Btn>
                </AZ>
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 2 — pending_seller_acceptance  (BUYER)
        ════════════════════════════════════════════════════════ */}
        {status === 'pending_seller_acceptance' && isBuyer && (
          <>
            {Summary({})}
            <StateDivider />
            {isClaim ? (
              // Dispatch (buyer in claim) reviews and accepts/rejects the driver's claim
              <>
                <div style={{ padding:'0 1rem' }}>
                  <MarchBox $a={pal.a}>
                    <PanelTitle $a={pal.a}>Driver Claim Request</PanelTitle>
                    <InfoLine $a={pal.a}>
                      <CheckCircle size={13} className="ico"/>
                      <span className="txt">A driver has claimed this job. Accept to confirm and begin the trip, or reject to repost.</span>
                    </InfoLine>
                  </MarchBox>
                </div>
                <AZ>
                  <Btn $v="primary" $a={pal.a} onClick={handleAccept} disabled={loading}>
                    {loading
                      ? <>Processing <Dots><span/><span/><span/></Dots></>
                      : <><Check size={14}/>Accept Claim</>}
                  </Btn>
                  <Btn $v="danger" onClick={()=>onRespond('reject')} disabled={loading}>
                    <X size={13}/>Reject Claim
                  </Btn>
                </AZ>
              </>
            ) : (
              <StatePanel>
                <WaitText $a={pal.a}>
                  {isSelfOrder ? <>Confirming your order<span className="cursor"/></> : <>Awaiting seller review<span className="cursor"/></>}
                </WaitText>
                <InfoLine $a={pal.a} style={{ marginTop:'8px' }}>
                  <Sparkles size={13} className="ico"/>
                  <span className="txt">{isSelfOrder ? 'Your self-order is being confirmed automatically…' : 'Order requested. Awaiting Acceptance'}</span>
                </InfoLine>
              </StatePanel>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 3 — seller_accepted  (BUYER pays)
        ════════════════════════════════════════════════════════ */}
        {status === 'seller_accepted' && isBuyer && (
          <>
            {Summary({})}
            <StateDivider />
            <StatePanel>
              {isSelfOrder ? (
                <InfoLine $a={pal.a}>
                  <ShieldCheck size={13} className="ico"/>
                  <span className="txt">Pay now to complete your self-order — <span className="bold">captured instantly</span>, no pickup needed.</span>
                </InfoLine>
              ) : isPickupPayment ? (
                <>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.35)', marginBottom: '0.55rem' }}>
                    Payment Options
                  </div>
                  <PayOptionsList>
                    {PAY_OPTIONS.map(opt => {
                      const handleValue = opt.handleKey ? sellerPaymentHandles?.[opt.handleKey] : null;
                      const displayLabel = opt.handleKey
                        ? (handleValue ? `${PAY_LABELS[opt.id]}: ${handleValue}` : opt.label)
                        : opt.label;
                      return (
                        <PayOptionRow key={opt.id} $active={buyerPaymentChoice === opt.id} $a={pal.a}>
                          <input
                            type="radio"
                            name="pickup-pay-option"
                            value={opt.id}
                            checked={buyerPaymentChoice === opt.id}
                            onChange={() => handleChoosePaymentMethod(opt.id)}
                          />
                          <span className="name">{displayLabel}</span>
                        </PayOptionRow>
                      );
                    })}
                  </PayOptionsList>
                  {buyerPaymentChoice && (
                    <InfoLine $a={pal.a} style={{ marginTop: '0.6rem' }}>
                      <CheckCircle size={13} className="ico"/>
                      <span className="txt">Seller notified — pay <span className="bold">{fmt(T.total)}</span> via {PAY_LABELS[buyerPaymentChoice]} at pickup.</span>
                    </InfoLine>
                  )}
                </>
              ) : (
                <InfoLine $a={pal.a}>
                  <ShieldCheck size={13} className="ico"/>
                  <span className="txt">Payment processed securely by Stripe and held in <span className="bold">escrow</span> until pickup.</span>
                </InfoLine>
              )}
            </StatePanel>
            {!isSelfOrder && isPickupPayment ? (
              <AZ>
                <Btn $v="ghost" $a={pal.a} disabled>
                  <Banknote size={14}/>
                  Awaiting seller confirmation at pickup
                </Btn>
              </AZ>
            ) : (
              <PaymentMethodPicker
                pal={pal} T={T} transactionId={transactionId} chat={chat} currency={currencyCode}
                buyerInfo={buyerInfo} onPayment={onPayment}
                payMethodChoice={payMethodChoice} setPayMethodChoice={setPayMethodChoice}
                showPay={showPay} setShowPay={setShowPay}
                isSelfOrder={isSelfOrder}
              />
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 3 — seller_accepted  (SELLER waiting / collect)
        ════════════════════════════════════════════════════════ */}
        {status === 'seller_accepted' && isSeller && (
          <>
            {Summary({ showPayout: true })}
            {isSelfOrder ? (
              // Self-order seller view: show payment form directly (no tab switching)
              <>
                <StateDivider />
                <StatePanel>
                  <InfoLine $a={pal.a}>
                    <ShieldCheck size={13} className="ico"/>
                    <span className="txt">Pay now to complete your self-order — <span className="bold">captured instantly</span>, no pickup needed.</span>
                  </InfoLine>
                </StatePanel>
                <PaymentMethodPicker
                  pal={pal} T={T} transactionId={transactionId} chat={chat} currency={currencyCode}
                  buyerInfo={buyerInfo} onPayment={onPayment}
                  payMethodChoice={payMethodChoice} setPayMethodChoice={setPayMethodChoice}
                  showPay={showPay} setShowPay={setShowPay}
                  isSelfOrder={isSelfOrder}
                />
              </>
            ) : (
              <>
                <StateDivider />
                <StatePanel>
                  <WaitText $a={pal.a}>
                    {isPickupPayment
                      ? (!buyerPaymentChoice
                          ? 'Waiting for buyer to choose a payment method'
                          : buyerPaymentChoice === 'card' ? 'Charge buyer card at pickup'
                          : `Collect ${PAY_LABELS[buyerPaymentChoice]} from buyer at pickup`)
                      : 'Waiting for buyer payment'}
                    <span className="cursor"/>
                  </WaitText>
                  {(!isPickupPayment || buyerPaymentChoice) && (
                    <InfoLine $a={pal.a} style={{ marginTop: '8px' }}>
                      <Banknote size={13} className="ico"/>
                      {isPickupPayment
                        ? <span className="txt">Confirm receipt of <span className="bold">{fmt(T.total)}</span> via {PAY_LABELS[buyerPaymentChoice]} to complete the order.</span>
                        : <span className="txt">Once the buyer pays, <span className="bold">{fmt(T.total)}</span> will be held in escrow until you complete the handoff.</span>
                      }
                    </InfoLine>
                  )}
                </StatePanel>
                {isPickupPayment && buyerPaymentChoice && buyerPaymentChoice !== 'card' && (
                  <AZ>
                    <Btn $v="primary" $a={pal.a} onClick={async () => {
                      try { await TransactionService.confirmCashPayment(transactionId, buyerPaymentChoice); }
                      catch(e) { alert('Error: ' + e.message); }
                    }}>
                      <Check size={14}/> Confirm {PAY_LABELS[buyerPaymentChoice]} Received
                    </Btn>
                  </AZ>
                )}
                {isPickupPayment && buyerPaymentChoice === 'card' && (
                  <PickupCardCollect transactionId={transactionId} pal={pal} T={T} onPayment={onPayment} chat={chat} buyerInfo={buyerInfo} currency={currencyCode} />
                )}
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 4 — paid  (BUYER)
        ════════════════════════════════════════════════════════ */}
        {status === 'paid' && isBuyer && (
          <>
            {/* Need-tab service: buyer IS the performer (driver/worker) */}
            {isServiceItem && isNeedTab ? (
              <>
                {Summary({})}
                <StateDivider />
                <StatePanel>
                  <PanelTitle $a={pal.a}>
                    {isRide ? 'Your Ride Assignment' : isMoving ? 'Your Moving Job' : 'Your Job'}
                  </PanelTitle>
                  <InfoLine $a={pal.a}>
                    <Zap size={13} className="ico"/>
                    <span className="txt">
                      Dispatch confirmed. Update your status as you go — dispatch auto-captures when complete.
                    </span>
                  </InfoLine>
                </StatePanel>
                {/* Service progress buttons for the performer (buyer on need-tab) */}
                {serviceProgress !== 'trip_complete' && (() => {
                  if (isRide) {
                    if (!serviceProgress)                return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('en_route')}     disabled={svcLoading}><Car size={16}/>En Route to Pickup</ServiceActionBtn>;
                    if (serviceProgress === 'en_route')  return <ServiceActionBtn $color="#34D399" onClick={() => handleSvcProgress('picking_up')}   disabled={svcLoading}><MapPin size={16}/>Picking Up Passenger</ServiceActionBtn>;
                    if (serviceProgress === 'picking_up')return <ServiceActionBtn $color="#FBBF24" onClick={() => handleSvcProgress('dropping_off')} disabled={svcLoading}><Navigation size={16}/>Dropping Off Passenger</ServiceActionBtn>;
                    if (serviceProgress === 'dropping_off') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')}  disabled={svcLoading}><Zap size={16}/>Complete Trip &amp; Auto Capture</ServiceActionBtn>;
                  }
                  if (isMoving) {
                    if (!serviceProgress)              return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('loading')}    disabled={svcLoading}><Truck size={16}/>Start Loading</ServiceActionBtn>;
                    if (serviceProgress === 'loading') return <ServiceActionBtn $color="#FBBF24" onClick={() => handleSvcProgress('delivering')} disabled={svcLoading}><Navigation size={16}/>In Transit</ServiceActionBtn>;
                    if (serviceProgress === 'delivering') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')} disabled={svcLoading}><Zap size={16}/>Complete Move &amp; Auto Capture</ServiceActionBtn>;
                  }
                  if (isDelivery) {
                    if (!serviceProgress)             return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('en_route')} disabled={svcLoading}><Navigation size={16}/>Start Delivery</ServiceActionBtn>;
                    if (serviceProgress === 'en_route') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')} disabled={svcLoading}><Zap size={16}/>Delivered — Auto Capture</ServiceActionBtn>;
                  }
                  // General service
                  if (!serviceProgress) return <ServiceActionBtn $color="#A78BFA" onClick={() => handleSvcProgress('job_started')}  disabled={svcLoading}><Wrench size={16}/>Start Service</ServiceActionBtn>;
                  return                         <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')}    disabled={svcLoading}><Zap size={16}/>Complete Service &amp; Auto Capture</ServiceActionBtn>;
                })()}
              </>
            ) : isServiceItem ? (
              /* Offer-tab service: buyer is passenger/customer — show progress tracker */
              <>
                {Summary({})}
                <StateDivider />
                <StatePanel>
                  <PanelTitle $a={pal.a}>
                    {isRide ? 'Your Ride' : isMoving ? 'Your Move' : 'Your Service'}
                  </PanelTitle>
                  <InfoLine $a={pal.a}>
                    <ShieldCheck size={13} className="ico"/>
                    <span className="txt">{fmt(T.total)} held securely in escrow — released when service completes.</span>
                  </InfoLine>
                </StatePanel>
                {/* Read-only progress tracker */}
                {isRide && (
                  <ProgressTrack>
                    {[
                      { lbl: 'Job Confirmed',     prog: null          },
                      { lbl: 'Driver En Route',   prog: 'en_route'    },
                      { lbl: 'Driver Picking Up', prog: 'picking_up'  },
                      { lbl: 'Dropping Off',      prog: 'dropping_off'},
                      { lbl: 'Trip Complete',     prog: 'trip_complete'},
                    ].map(({ lbl, prog }) => {
                      const ORDER = [null,'en_route','picking_up','dropping_off','trip_complete'];
                      const cur = ORDER.indexOf(serviceProgress);
                      const self = ORDER.indexOf(prog);
                      const done   = self < cur || (self === cur && prog !== null);
                      const active = self === cur && prog !== null && prog === serviceProgress;
                      return (
                        <ProgressStep key={lbl} $done={done} $active={active} $a={pal.a}>
                          <div className="dot" />
                          <div className="lbl">{lbl}</div>
                        </ProgressStep>
                      );
                    })}
                  </ProgressTrack>
                )}
              </>
            ) : (
              /* Standard (non-service) buyer paid view — compact item strip + QR */
              <>
                {/* Compact item thumbnails + total */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.6rem 1rem',
                  borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                  {items.map((item, i) => {
                    const id    = item.itemId || item.id || `item-${i}`;
                    const thumb = item.itemImage || item.imageUrl;
                    const isLoaded = loadedImgs.has(id);
                    return (
                      <div
                        key={id}
                        onClick={() => thumb && openZoom(thumb)}
                        style={{
                          position: 'relative',
                          width: 40, height: 40, borderRadius: 8, overflow: 'hidden', flexShrink: 0,
                          border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)',
                          cursor: thumb ? 'zoom-in' : 'default',
                        }}>
                        {thumb
                          ? <>
                              {!isLoaded && <ShimmerOverlay />}
                              <img
                                src={thumb}
                                alt=""
                                loading="lazy"
                                decoding="async"
                                style={{ width:'100%', height:'100%', objectFit:'cover', position:'relative', opacity: isLoaded ? 1 : 0, transition: 'opacity 0.35s ease' }}
                                onLoad={() => markImgLoaded(id)}
                                onError={e => { e.target.style.display='none'; markImgLoaded(id); }}
                              />
                            </>
                          : <Package size={18} style={{ margin:'11px auto', display:'block', color:'rgba(255,255,255,0.3)' }} />}
                      </div>
                    );
                  })}
                  {items.length > 1 && (
                    <div style={{ fontSize:'0.72rem', color:'rgba(255,255,255,0.4)', whiteSpace:'nowrap' }}>
                      {items.length} items
                    </div>
                  )}
                  <div style={{ marginLeft:'auto', fontSize:'0.9rem', fontWeight:700, color:'rgba(255,255,255,0.85)' }}>
                    {fmt(T.total)}
                  </div>
                </div>

                {/* QR code block — card payments */}
                {!isSelfOrder && transaction?.transactionCode && transaction?.paymentStatus !== 'cash_on_pickup' && (
                  <div style={{
                    margin: '0.75rem 1rem',
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${pal.a}30`,
                    borderRadius: 12,
                    padding: '1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.6rem',
                  }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.07em', textTransform:'uppercase', color: pal.a }}>
                      <QrCode size={13} />
                      Your Pickup Code
                    </div>
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=190x190&data=${encodeURIComponent(transaction.qrCodeUrl || '')}`}
                      alt="Pickup QR Code"
                      width={190}
                      height={190}
                      style={{ borderRadius: 8, background: '#fff', padding: 6, display: 'block' }}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = `https://quickchart.io/qr?text=${encodeURIComponent(transaction.qrCodeUrl || '')}&size=190`;
                      }}
                    />
                    <div style={{
                      fontFamily: 'SF Mono, Monaco, Courier New, monospace',
                      fontSize: '1.1rem',
                      fontWeight: 700,
                      letterSpacing: '0.12em',
                      color: 'rgba(255,255,255,0.9)',
                    }}>
                      {transaction.transactionCode}
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
                      Show to seller at pickup to complete the order
                    </div>
                  </div>
                )}

                {/* Cash payment — no QR, just bring cash reminder */}
                {!isSelfOrder && transaction?.paymentStatus === 'cash_on_pickup' && (
                  <div style={{
                    margin: '0.75rem 1rem',
                    background: 'rgba(255,255,255,0.03)',
                    border: `1px solid ${pal.a}30`,
                    borderRadius: 12,
                    padding: '1.25rem 1rem',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '0.5rem',
                  }}>
                    <Banknote size={32} style={{ color: pal.a }} />
                    <div style={{ fontWeight: 700, fontSize: '1rem', color: 'rgba(255,255,255,0.9)' }}>
                      Bring <strong>{fmt(T.total)} cash</strong>
                    </div>
                    <div style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.45)', textAlign: 'center' }}>
                      Pay the seller in cash at pickup
                    </div>
                  </div>
                )}

                {/* Self-order completing indicator */}
                {isSelfOrder && (
                  <StatePanel>
                    <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                      <CheckMark color={pal.a} size={38} />
                      <div>
                        <PanelTitle $a={pal.a}>Payment Captured</PanelTitle>
                        <InfoLine $a={pal.a}>
                          <Sparkles size={13} className="ico"/>
                          <span className="txt">
                            {selfCompleting
                              ? <>Completing your order <Dots><span/><span/><span/></Dots></>
                              : `${fmt(T.total)} paid — completing order automatically…`}
                          </span>
                        </InfoLine>
                      </div>
                    </div>
                  </StatePanel>
                )}
              </>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 4 — paid  (SELLER)
        ════════════════════════════════════════════════════════ */}
        {status === 'paid' && isSeller && (
          <>
            {Summary({ showPayout: true })}
            <StateDivider />
            {/* Offer-tab service: seller IS the performer (driver/provider) */}
            {isServiceItem && !isNeedTab ? (
              <>
                <StatePanel>
                  <PanelTitle $a={pal.a}>
                    {isRide ? 'Ride In Progress' : isMoving ? 'Moving Job In Progress' : 'Service In Progress'}
                  </PanelTitle>
                  <InfoLine $a={pal.a}>
                    <Banknote size={13} className="ico"/>
                    <span className="txt"><span className="bold">{fmt(T.total)}</span> in escrow — auto-captured when you complete the service.</span>
                  </InfoLine>
                </StatePanel>
                {/* 1-hr reminder banner duplicated here for seller */}
                {showRideReminder && (
                  <ReminderBanner>
                    <Bell size={15} className="bell" />
                    <div className="body"><span className="hi">Reminder:</span> pickup is within 1 hour. Head to the pickup location.</div>
                  </ReminderBanner>
                )}
                {/* Service progress action buttons */}
                {serviceProgress !== 'trip_complete' && (() => {
                  if (isRide) {
                    if (!serviceProgress)                return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('en_route')}     disabled={svcLoading}><Car size={16}/>En Route to Pickup</ServiceActionBtn>;
                    if (serviceProgress === 'en_route')  return <ServiceActionBtn $color="#34D399" onClick={() => handleSvcProgress('picking_up')}   disabled={svcLoading}><MapPin size={16}/>Picking Up Passenger</ServiceActionBtn>;
                    if (serviceProgress === 'picking_up')return <ServiceActionBtn $color="#FBBF24" onClick={() => handleSvcProgress('dropping_off')} disabled={svcLoading}><Navigation size={16}/>Dropping Off Passenger</ServiceActionBtn>;
                    if (serviceProgress === 'dropping_off') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')}  disabled={svcLoading}><Zap size={16}/>Complete Trip &amp; Auto Capture</ServiceActionBtn>;
                  }
                  if (isMoving) {
                    if (!serviceProgress)              return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('loading')}    disabled={svcLoading}><Truck size={16}/>Start Loading</ServiceActionBtn>;
                    if (serviceProgress === 'loading') return <ServiceActionBtn $color="#FBBF24" onClick={() => handleSvcProgress('delivering')} disabled={svcLoading}><Navigation size={16}/>In Transit</ServiceActionBtn>;
                    if (serviceProgress === 'delivering') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')} disabled={svcLoading}><Zap size={16}/>Complete Move &amp; Auto Capture</ServiceActionBtn>;
                  }
                  if (isDelivery) {
                    if (!serviceProgress)             return <ServiceActionBtn $color="#60A5FA" onClick={() => handleSvcProgress('en_route')} disabled={svcLoading}><Navigation size={16}/>Start Delivery</ServiceActionBtn>;
                    if (serviceProgress === 'en_route') return <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')} disabled={svcLoading}><Zap size={16}/>Delivered — Auto Capture</ServiceActionBtn>;
                  }
                  // General service (home repair, cleaning, tutoring, beauty, etc.)
                  if (!serviceProgress) return <ServiceActionBtn $color="#A78BFA" onClick={() => handleSvcProgress('job_started')}  disabled={svcLoading}><Wrench size={16}/>Start Service</ServiceActionBtn>;
                  return                         <ServiceActionBtn $color="#4ADE80" onClick={() => handleSvcProgress('complete')}    disabled={svcLoading}><Zap size={16}/>Complete Service &amp; Auto Capture</ServiceActionBtn>;
                })()}
                {/* Trip summary panel (shows briefly before auto-complete kicks in) */}
                {serviceProgress === 'trip_complete' && tripSummary && (
                  <TripSummaryCard>
                    <div className="title"><Flag size={14}/>Trip Summary</div>
                    {tripSummary.durationMinutes && (
                      <div className="row"><span className="lbl">Duration</span><span className="val">{tripSummary.durationMinutes} min</span></div>
                    )}
                    {tripSummary.distance && (
                      <div className="row"><span className="lbl">Distance</span><span className="val">{tripSummary.distance}</span></div>
                    )}
                    <div className="row">
                      <span className="lbl">Completed</span>
                      <span className="val">
                        {tripSummary.completionTime ? (() => { try { return new Date(tripSummary.completionTime).toLocaleTimeString([], { timeStyle:'short' }); } catch { return '—'; } })() : '—'}
                      </span>
                    </div>
                    <div className="divider" />
                    <div className="total-row">
                      <span className="total-lbl">Due Amount</span>
                      <span className="total-val">{fmt(tripSummary.amount || T.total)}</span>
                    </div>
                  </TripSummaryCard>
                )}
              </>
            ) : isServiceItem && isNeedTab ? (
              /* Need-tab: seller is dispatch — monitor driver progress */
              <>
                <StatePanel>
                  <PanelTitle $a={pal.a}>Dispatch View</PanelTitle>
                  <InfoLine $a={pal.a}>
                    <Banknote size={13} className="ico"/>
                    <span className="txt"><span className="bold">{fmt(T.total)}</span> in escrow — auto-captured when driver completes the service.</span>
                  </InfoLine>
                </StatePanel>
                {isRide && (
                  <ProgressTrack>
                    {[
                      { lbl: 'Assigned',          prog: null           },
                      { lbl: 'Driver En Route',   prog: 'en_route'     },
                      { lbl: 'Picking Up',        prog: 'picking_up'   },
                      { lbl: 'Dropping Off',      prog: 'dropping_off' },
                      { lbl: 'Trip Complete',     prog: 'trip_complete'},
                    ].map(({ lbl, prog }) => {
                      const ORDER = [null,'en_route','picking_up','dropping_off','trip_complete'];
                      const cur = ORDER.indexOf(serviceProgress);
                      const self = ORDER.indexOf(prog);
                      const done   = self < cur || (self === cur && prog !== null);
                      const active = prog !== null && prog === serviceProgress;
                      return (
                        <ProgressStep key={lbl} $done={done} $active={active} $a={pal.a}>
                          <div className="dot" />
                          <div className="lbl">{lbl}</div>
                        </ProgressStep>
                      );
                    })}
                  </ProgressTrack>
                )}
                {/* Trip summary for dispatch once driver completes */}
                {serviceProgress === 'trip_complete' && tripSummary && (
                  <TripSummaryCard>
                    <div className="title"><Flag size={14}/>Trip Summary</div>
                    {tripSummary.durationMinutes && <div className="row"><span className="lbl">Duration</span><span className="val">{tripSummary.durationMinutes} min</span></div>}
                    {tripSummary.distance && <div className="row"><span className="lbl">Distance</span><span className="val">{tripSummary.distance}</span></div>}
                    <div className="row"><span className="lbl">Completed</span><span className="val">{tripSummary.completionTime ? (() => { try { return new Date(tripSummary.completionTime).toLocaleTimeString([], { timeStyle:'short' }); } catch { return '—'; } })() : '—'}</span></div>
                    <div className="divider" />
                    <div className="total-row"><span className="total-lbl">Due to Driver</span><span className="total-val">{fmt(tripSummary.amount || T.total)}</span></div>
                  </TripSummaryCard>
                )}
              </>
            ) : (
              /* Standard (non-service) seller paid view */
              <StatePanel>
                <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                  <CheckMark color={pal.a} size={38} />
                  <div>
                    <PanelTitle $a={pal.a}>Payment Received</PanelTitle>
                    <InfoLine $a={pal.a}>
                      <Banknote size={13} className="ico"/>
                      <span className="txt"><span className="bold">{fmt(T.total)}</span> in escrow. Share a pickup location — funds release once the buyer scans your code.</span>
                    </InfoLine>
                  </div>
                </div>
              </StatePanel>
            )}
          </>
        )}

        {/* ════════════════════════════════════════════════════════
            STATE 5 — completed
        ════════════════════════════════════════════════════════ */}
        {status === 'completed' && (
          <>
            {isSeller ? Summary({ showPayout: true }) : Summary({})}
            <StateDivider />
            <StatePanel>
              <div style={{ display:'flex', alignItems:'flex-start', gap:'0.75rem' }}>
                <CheckMark color={pal.a} size={38} />
                <div style={{ flex:1 }}>
                  <PanelTitle $a={pal.a}>
                    {isServiceItem
                      ? (isSeller ? (isNeedTab ? 'Service Complete · Driver Paid' : 'Service Complete · Funds Released') : (isNeedTab ? 'Job Complete!' : 'Service Complete!'))
                      : (isSeller ? 'Order Fulfilled · Funds Released' : 'Order Complete!')}
                  </PanelTitle>
                  {isSeller && transaction?.sellerPayout ? (
                    <PayRow className="hi">
                      <span className="l">{isNeedTab ? 'Driver payout' : 'Amount received'}</span>
                      <span className="v">{fmt(T.total)}</span>
                    </PayRow>
                  ) : (
                    <InfoLine $a={pal.a}>
                      <Sparkles size={13} className="ico"/>
                      <span className="txt">
                        {isServiceItem
                          ? (isBuyer ? (isNeedTab ? 'Job completed — dispatch will process your payment.' : 'Service completed. Thanks for using KalKode!') : 'Transaction closed and funds released.')
                          : (isBuyer ? 'Your order is complete. Thanks for shopping on KalKode!' : 'Transaction complete and funds have been released.')}
                      </span>
                    </InfoLine>
                  )}
                </div>
              </div>
            </StatePanel>
            {/* Final trip summary on completed state */}
            {isServiceItem && tripSummary && (
              <TripSummaryCard>
                <div className="title"><Timer size={14}/>Trip Summary</div>
                {tripSummary.durationMinutes && <div className="row"><span className="lbl">Duration</span><span className="val">{tripSummary.durationMinutes} min</span></div>}
                {tripSummary.distance && <div className="row"><span className="lbl">Distance</span><span className="val">{tripSummary.distance}</span></div>}
                <div className="row"><span className="lbl">Completed</span><span className="val">{tripSummary.completionTime ? (() => { try { return new Date(tripSummary.completionTime).toLocaleTimeString([], { timeStyle:'short' }); } catch { return '—'; } })() : '—'}</span></div>
                <div className="divider" />
                <div className="total-row"><span className="total-lbl">Total</span><span className="total-val">{fmt(tripSummary.amount || T.total)}</span></div>
              </TripSummaryCard>
            )}
          </>
        )}

        </>}
      </Inner>
    </Card>
    {zoomImage && createPortal(
      <ZoomOverlay $closing={zoomClosing} onClick={closeZoom}>
        <ZoomCloseBtn onClick={(e) => { e.stopPropagation(); closeZoom(); }}><X size={18}/></ZoomCloseBtn>
        <ZoomImg src={zoomImage} alt="" $closing={zoomClosing} onClick={e => e.stopPropagation()} />
      </ZoomOverlay>,
      document.body
    )}
    {mapPickerFieldKey && userLocation && createPortal(
      <TreasureMap
        isOpen={!!mapPickerFieldKey}
        onClose={() => setMapPickerFieldKey(null)}
        items={[]}
        services={[]}
        userLocation={userLocation}
        selectionMode={true}
        onLocationSelect={handleMapLocationPicked}
        onRequestLocation={() => requestLocation()}
      />,
      document.body
    )}
    </>
  );
};

export default UnifiedCartWindow;
